/*
    N.B. Keep this comment to preserve line number alignment among locale files:
    Same line = same translation entry
 */

import { FeeTarget } from '@lib/shared';

import { AssetDoors, AssetEuroNorm, AssetFuel, AssetGearboxType, AssetPartPosition, AssetSeatUpholstery, AssetTachograph, AssetType, AssetUseType, AssetWheelchairRamp, AuctionState, BackendErrorCode, BusType, ChargingType, CngTanksMaterials, CruiseControlType, DeckType, DirectSaleState, FiveLevelStateOfUse, Glasses, NegotiationState, OperatorType, OrderState, PaymentProviderActionResult, RequireScrappingFilter, SellingMethod, ThreeLevelStateOfUse, ValueAddedServiceCode, VehicleCondition, VehicleRemark } from '~/apiClient';
import { PlatformBusClass } from '~/types/asset.type';
import { SaleBundleType } from '~/types/sale.type';

const en = {
    account: {
        list: {
            results: '{total} result | {total} results',
        },
        bids: {
            title: 'Your Bids',
            emptyState: {
                base: "You haven't placed a bid in any auction yet,",
                cta: 'Participate in auctions',
            },
            filters: {
                bidPosition: {
                    base: 'Bid position',
                    anyOption: 'All bid positions',
                    maxBidder: 'Your bid is the highest',
                    outbid: 'You have been outbid',
                },
                state: {
                    base: 'Auction state',
                    anyOption: 'All auction states',
                    [AuctionState.Expired]: 'Closed auctions',
                    [AuctionState.Running]: 'Live auctions',
                },
            },
        },
        myAuctions: {
            title: 'Your Vehicles at Auction',
            emptyStateText: 'There are no auctions with a seller associated with this account.',
        },
        negotiations: {
            directSaleBuyer: {
                title: 'Your Purchase Negotiations in Buy Now',
                emptyState: {
                    text: "You haven't made your first offer in a Buy NowBuy Now.",
                    cta: 'Participate in Buy Now',
                },
            },
            filters: {
                state: {
                    base: 'Negotiation state',
                    anyOption: 'All vehicles',
                },
            },
        },
        myDirectSales: {
            title: 'Your Vehicles in Buy Now',
            emptyStateText: 'There are no vehicles in Buy Now with a seller associated with this account.',
        },
        orders: {
            title: 'Order history',
            filters: {
                sellingMethod: {
                    base: 'Selling method',
                    anyOption: 'All Sales',
                    [SellingMethod.Auction]: 'Auction',
                    [SellingMethod.DirectSale]: 'Buy Now',
                },
            },
            emptyState: "You haven't ordered anything yet.",
            action: 'Order details',
            edit: 'Buy additional services',
        },
        order: {
            title: 'Order: {title}',
            summary: 'Summary',
        },
        favorites: {
            title: 'Favorite vehicles',
            emptyStateText: 'No favorite vehicles available.',
            sorting: {
                createdAtDesc: 'Saved most recently',
                createdAtAsc: 'Saved least recently',
            },
        },
    },
    paymentMethods: {
        addNew: 'Add new',
        addNewPaymentMethod: 'Add payment method',
        title: 'Payment Methods',
        empty: 'You have no active payment methods',
        updateExpiration: 'Update expiration',
        card: {
            expires: 'Exp: {month}/{year}',
            expired: 'Expired on {month}/{year}',
        },
        addModal: {
            title: 'Add a payment method',
            submit: 'Add new method',
            error: 'An error occurred while adding payment method, please try again later or contact support',
        },
        deleteModal: {
            title: 'You are about to delete',
            subtitle: 'Once you delete this payment method, you will no longer be able to use it on Fleequid',
            confirmButton: 'Delete method',
            cancelButton: 'Cancel',
        },
        updateExpirationModal: {
            title: 'Update expiration',
            month: 'Month',
            year: 'Year',
            confirmButton: 'Update',
            cancelButton: 'Cancel',
        },
    },
    common: {
        logout: 'Logout',
        login: 'Login',
        loginSignup: 'Login/Signup',
        profile: 'Your Profile',
        searchPlaceholder: 'Search...',
        searchNoResults: 'No results found for "{query}"',
        email: 'Email',
        yourAccount: 'Your Account',
        language: 'Language',
        showMore: 'Show more',
        showLess: 'Show less',
        delete: 'Delete',
        back: 'Go back',
        goToAuction: 'Go to auction',
        goToDirectSale: 'Go to Buy Now',
        goToNegotiation: 'Go to negotiation',
        incrementIdLabel: 'Reference',
    },
    countdown: {
        startsIn: 'Starts in',
        endsIn: 'Ends in',
        days: 'd',
        hours: 'h',
        minutes: 'm',
        seconds: 's',
    },
    cookie: {
        name: 'Name',
        provider: 'Provider',
        purpose: 'Purpose',
        expiry: 'Expiry',
        type: 'Type',
    },
    error: {
        backend: {
            [BackendErrorCode.InvalidEnumValue]: 'Invalid data type',
            [BackendErrorCode.InvalidDate]: 'Invalid date format',
            [BackendErrorCode.InvalidLength]: 'Invalid length. Verify the string for missing or extra characters',
            [BackendErrorCode.NotAString]: 'Invalid data type; only letters are allowed',
            [BackendErrorCode.EmptyString]: 'Missing Information. Please complete all required data',
            [BackendErrorCode.PrismaError]: 'The data could not be saved; please try a different value',
            [BackendErrorCode.AuthAuthenticationVersionMismatch]: 'Your session has expired. Please log in again to continue',
            [BackendErrorCode.AuthNoUserByEmail]: 'There is no account associated with the email address entered.',
            [BackendErrorCode.AuthWrongPassword]: 'The password entered is incorrect.',
            [BackendErrorCode.UserIsBanned]: 'Your account has been suspended. Contact us for further information.',
            [BackendErrorCode.AuthEmailNotVerified]: 'The email address entered is not yet verified. We sent you a new email verification link.',
            [BackendErrorCode.SignupUserAlreadyExists]: 'Email address already registered, if you have forgotten your password try to recover it.',
            [BackendErrorCode.UploadFileError]: 'Error while uploading your file',
            [BackendErrorCode.IsNotTrue]: 'Missing selection: Choose the required option to continue',
            [BackendErrorCode.EntityNotFound]: 'No Results Found',
            [BackendErrorCode.PasswordResetEmailNotExists]: 'There is no account associated with this email',
            [BackendErrorCode.EmailChangeWrongPassword]: 'You cannot change the email address because the password is not correct',
            [BackendErrorCode.CantPerformUserCompanyEqSellerCompany]: 'This asset is from your company, you cannot buy it.',
            [BackendErrorCode.AuctionUpdateDenied]: 'Only seller company users are authorized for this action',
            authentication: 'Authentication error; please try logging in again',
        },
        '404': {
            title: '404',
            subtitle: 'Oops! Page not found',
            description: "Sorry, the page you requested does not exist. Anyway you'll find plenty more to explore on Fleequid.",
            button: 'Back to homepage',
        },
        '500': {
            title: '500',
            subtitle: 'Oops! An error occurred',
            description: 'We apologize for the inconvenience. We are already working on the problem.',
            help: {
                base: 'In the meantime you can try later or {contactUs}',
                contactUs: 'contact us at {email}, or {phone} for emergencies',
            },
            button: 'Back to homepage',
        },
        default: {
            title: 'Error',
            subtitle: 'We have encountered an unexpected error',
            description: 'We are already working to solve the problem.',
            button: 'Back to homepage',
        },
    },
    a11y: {
        auctionDetail: 'Learn more about {name}',
        directSaleDetail: 'Learn more about {name}',
    },
    sdp: {
        multi: {
            title: 'Bundle of {count} vehicles',
            detailTitle: 'Specifications of the {name}',
        },
        share: {
            label: 'Share',
            copied: 'Copied!',
            text: 'I would like to share this vehicle with you',
        },
        imageGallery: 'Complete gallery ({totalImages})',
        videoGallery: 'Watch videos ({totalVideos})',
        imagePagination: 'Photo {0} of {1}',
        videoPagination: 'Video {0} of {1}',
        papers: 'Documents',
        registrationDocument: 'Vehicle registration document',
        cocDocument: 'Certificate of Conformity (COC)',
        cocDocumentAbsent: 'The certificate of conformity is not available.',
        maintenanceCard: 'Maintenance history',
        detailsTitle: 'Vehicle details and damage | Vehicle details and damage',
        needSupport: 'Do you need support?',
        contactUs: 'Contact Fleequid customer service',
        notice: {
            inspectionExpired: 'To export this vehicle it is necessary to carry out the inspection.',
            fee: {
                base: 'fees ({0})',
                link: 'what are they?',
            },
        },
        inspectionExpiredModal: {
            title: 'Inspection required for purchase outside of Italy',
            description: 'This vehicle is located in Italy, and according to the Road Code in effect there, vehicles with expired inspections cannot be exported outside of the country. You can participate in the auction and win, but in order to obtain the vehicle, you will need to:',
            item1: 'Carry out the inspection before export',
            item2: 'Bear any applicable costs',
            item3: 'Complete the deregistration procedure for permanent export',
            askConfirm: 'Do you still want to proceed to lock the minimum amount?',
            confirmButton: 'Confirm and proceed',
        },
        bail: {
            error: 'An error occurred while processing the transaction, please try again later or contact support',
            submit: 'Bind the security deposit',
            timeoutWarning: 'You have {time} to complete the operation',
        },
        bottomSheet: {
            openSheetCta: 'Show purchase options',
            sheetTitle: 'Purchase options',
        },
        vas: {
            title: 'Additional services (optional)',
            notAvailable: 'Not available',
            guestDisclaimer: 'Some services may not be available for this vehicle in your country. {loginOrSignup} to verify.',
            loginOrSignup: 'Login or Signup',
            tooltip: {
                [ValueAddedServiceCode.LogisticsService]: 'Fleequid can manage the transport safely, ensuring your vehicle arrives at its destination without risk',
                [ValueAddedServiceCode.ProvisionalLicensePlate]: 'Issuance of a temporary license plate while waiting for the new registration',
                [ValueAddedServiceCode.VehicleNationalization]: 'Compliance process required to operate the vehicle in your territory (only for vehicles purchased abroad)',
                [ValueAddedServiceCode.VehicleRegistration]: 'Obtain the final license plate for your vehicle (vehicle nationalization is also required)',
                [ValueAddedServiceCode.PowertrainWarranty]: 'Add up to 12 months of warranty covering potential engine and gearbox failures',
                [ValueAddedServiceCode.FleequidCare]: 'Coverage up to 3 months for potential engine and gearbox failures',
            },
            selection: {
                compute: 'Calculate',
                update: 'Edit',
            },
            logisticsModal: {
                title: 'Calculate vehicle delivery',
                description: 'If you want to receive your vehicle directly at the address of your choice through our transport service, you can purchase it and choose where the vehicle will be delivered, without worries or complications.',
                confirm: 'Confirm',
            },
            powertrainWarrantyModal: {
                title: 'Powertrain Warranty',
                description1: {
                    base: 'The {bold1} is an additional warranty that protects your purchase from any {bold2} not already reported in the {highlight}.',
                    bold1: 'Powertrain Warranty',
                    bold2: 'engine and transmission damage',
                    highlight: 'TrustReport',
                },
                description2: {
                    base: 'You can choose coverage from {bold1}, with a {bold2}, to eliminate the risk of unexpected costs after purchase and drive with greater peace of mind.',
                    bold1: '{from} to {to} months',
                    bold2: 'coverage limit up to {refound}',
                },
                termsAndConditions: {
                    base: '{link} apply.',
                    link: 'Terms and conditions',
                },
                confirm: 'Confirm',
            },
        },
        trustReport: {
            inspectedBy: 'Inspected by',
            tooltip: "All available information and images are the result of Fleequid's independent inspection of the vehicle.",
            description: "All available information and images are the result of Fleequid's independent inspection of the vehicle. The TrustReport guarantees the accuracy of the information provided and the condition of the vehicle as photographed.",
        },
        [SellingMethod.Auction]: {
            bail: {
                title: 'Confirm your bid',
                p1: '<strong>Congratulations!</strong> You are just a few clicks away from purchasing your next vehicle!',
                p2: 'To <strong>confirm your bid</strong> securely, we invite you to add a validated payment card as a <strong>form of guarantee</strong>.',
                amountTitle: 'COMPLETE THE PRE-AUTHORIZATION AND CONFIRM YOUR BID',
                p3: '<strong>The pre-authorization</strong> temporarily holds the amount on your card as a guarantee, but <strong>no amount will be charged</strong>.',
                p4: 'It will be automatically released if your bid is not accepted.',
                p5: '<strong>Note</strong>: Vehicles are awarded to the highest bidder. Your bid may be surpassed.',
                submitCta: 'Complete the pre-authorization',
            },
            bid: {
                title: 'Become the top bidder now',
                description: 'Remember that the single or maximum bid is binding.',
                autoBidEditTitle: 'Update your Auto Bid',
                autoBidEditDescription: 'Remember that the maximum bid is binding.',
                activeAutoBid: 'Auto Bid active until',
                actualBid: 'Actual bid',
                autoBidToggle: 'Auto Bid ({0})',
                autoBidLink: 'what is it?',
                yourBid: 'Your bid',
                yourMaxBid: 'Your maximum bid',
                restoreMinBid: 'Reset',
                confirmSingleBid: 'Place your bid',
                confirmAutoBid: 'Activate Auto Bid',
                confirmAutoBidUpdate: 'Update Auto Bid',
                cancel: 'Cancel',
                success: 'Bid successfully placed!',
                outbid: 'Another user has outbid you',
                expiredError: {
                    title: 'The auction is closed',
                    description: 'No further bids can be placed.',
                },
                bidError: {
                    title: 'An unexpected error has occurred.',
                    description: 'Your bid could not be placed. Please refresh the page and try placing your bid again.',
                },
            },
            bidButton: 'Place a bid',
            bidButtonActiveAutoBid: 'Update Auto Bid',
            state: {
                [AuctionState.Draft]: 'Draft Auction',
                [AuctionState.ToBeApproved]: 'Auction to be approved',
                [AuctionState.ToBeScheduled]: 'Auction to be scheduled',
                [AuctionState.Waiting]: 'Auction to be published',
                [AuctionState.Expired]: 'Closed auction',
                [AuctionState.Canceled]: 'Canceled auction',
            },
            notice: {
                cantPerform: {
                    [BackendErrorCode.CantPerformScrapSaleNotScrapyardCompany]: 'Auction reserved for scrapyard workers registered in the register of waste managers in cat. 5 and managers of the C.E.R. code 16.01.04.',
                    [BackendErrorCode.CantPerformUserCompanyEqSellerCompany]: 'The vehicle is being sold by your company.',
                    [BackendErrorCode.CantPerformUnapprovedCompany]: 'You cannot place bids because we are reviewing your company’s documents.',
                    [BackendErrorCode.CantPerformCompanyUnderReview]: "You cannot place bids because we are reviewing your company's documents.",
                    [BackendErrorCode.CantPerformRegisteredCompany]: "You cannot place bids because we are verifying your company's data.",
                    [BackendErrorCode.CantPerformUserWithoutCompany]: "You cannot place bids because we haven't found any company associated with your profile.",
                    [BackendErrorCode.CantPerformCompanyNotYetApprovedForScrap]: 'You cannot place bids because we are verifying your registration in the waste management register (category 5) and for C.E.R. code 16.01.04.',
                    [BackendErrorCode.CantPerformCompanyNotApprovedForScrap]: 'You cannot place bids because your company is not registered in the waste management register (category 5) or for C.E.R. code 16.01.04.',
                    generic: 'You cannot participate in this auction',
                },
            },
            auctionStatus: {
                sellerReservePrice: 'Minimum Commitment Sale Price',
                startingPrice: 'Starting price',
                current: 'Current',
                wonAuction: 'Auction won!',
                maxBidder: 'Your bid is the highest!',
                outBid: 'You have been outbid!',
                negotiationInProgress: 'Negotiation in progress',
            },
            draftConfirmation: {
                title: 'You are in draft mode',
                description: 'Check all the auction details and confirm the accuracy of the entered data.',
                action: 'Approve listing',
                loginAction: 'Log in to confirm',
                success: {
                    title: 'You have confirmed the listing',
                    description: 'The data confirmation has been sent successfully',
                },
            },
            related: {
                auctions: 'Related vehicles',
                directSales: 'Related vehicles in Buy Now',
            },
        },
        [SellingMethod.DirectSale]: {
            directSaleStatus: {
                sellerReservePrice: 'Minimum Commitment Sale Price',
                askingPrice: 'Asking price',
                notAvailable: 'Not Available',
                sold: 'Sold',
                purchased: 'Purchased',
                inNegotiation: 'In negotiation',
                annotation: {
                    comingSoon: 'Coming soon',
                    outstanding: 'Pending',
                    notPaid: 'Unpaid purchase',
                    pendingPayment: 'Pending payment',
                    unsold: 'Unsold',
                },
            },
            draftConfirmation: {
                title: 'You are in draft mode',
                description: 'Check all the details of the sale and confirm the accuracy of the information entered.',
                action: 'Approve listing',
                loginAction: 'Log in to confirm',
                success: {
                    title: 'You have confirmed the listing',
                    description: 'The details have been successfully confirmed',
                },
            },
            notice: {
                cantPerform: {
                    [BackendErrorCode.CantPerformScrapSaleNotScrapyardCompany]: 'Sale reserved for scrap operators in the register of category 5 waste managers and managers of the CER code 16.01.04.',
                    [BackendErrorCode.CantPerformUserCompanyEqSellerCompany]: 'The vehicle is sold by your company.',
                    [BackendErrorCode.CantPerformUnapprovedCompany]: "You are not authorised to make a purchase because we are verifying your company's documents.",
                    [BackendErrorCode.CantPerformCompanyUnderReview]: "You are not authorised to make a purchase because we are verifying your company's documents.",
                    [BackendErrorCode.CantPerformRegisteredCompany]: "You are not authorised to make a purchase because we are verifying your company's data.",
                    [BackendErrorCode.CantPerformUserWithoutCompany]: 'You are not authorised to make a purchase because we have not found any company associated with your profile.',
                    [BackendErrorCode.CantPerformCompanyNotYetApprovedForScrap]: 'You are not authorised to make a purchase because we are verifying your registration in the register of waste managers (category 5) and for CER code 16.01.04.',
                    [BackendErrorCode.CantPerformCompanyNotApprovedForScrap]: 'You are not authorised to make a purchase because your company is not registered in the register of waste managers (category 5) or for CER code 16.01.04.',
                    generic: 'You are not authorised to make a purchase',
                },
            },
            order: {
                title: 'Place your order',
                description: 'Congratulations, you are one step away from your next vehicle!',
                price: 'Price',
                cancel: 'Cancel',
                button: 'Buy now',
                orderError: {
                    title: 'An unexpected error occurred.',
                    description: 'The order could not be completed. Please refresh the page and try again.',
                },
                expiredError: {
                    title: 'Buy Now are closed',
                    description: 'It is not possible to place orders.',
                },
                bail: {
                    title: 'Confirm your order',
                    p1: '<strong>Congratulations!</strong> You are just a few clicks away from purchasing your next vehicle!',
                    p2: 'To <strong>reserve the vehicle</strong> and <strong>confirm your order</strong> securely, we invite you to add a validated payment card as a <strong>form of guarantee</strong>.',
                    amountTitle: 'COMPLETE THE PRE-AUTHORIZATION AND CONFIRM YOUR ORDER',
                    p3: '<strong>The pre-authorization</strong> temporarily holds the amount on your card as a guarantee, but <strong>no amount will be charged</strong>.',
                    p4: '<strong>Note</strong>: The vehicle remains available to other buyers until the order is formally submitted.',
                    submitCta: 'Complete the pre-authorization',
                },
            },
            orderButton: 'Buy now',
            negotiation: {
                bail: {
                    title: 'Secure the deposit to confirm your offer',
                    p1: '<strong>Congratulations!</strong> You are just a few clicks away from purchasing your next vehicle!',
                    p2: 'To <strong>confirm your offer</strong> securely, we invite you to add a validated payment card as a <strong>form of guarantee</strong>.',
                    amountTitle: 'COMPLETE THE PRE-AUTHORIZATION AND CONFIRM YOUR OFFER',
                    p3: '<strong>The pre-authorization</strong> temporarily holds the amount on your card as a guarantee, but <strong>no amount will be charged</strong>.',
                    p4: 'It will be automatically released if your offer is not accepted.',
                    p5: '<strong>Note</strong>: The vehicle remains available to other buyers until your offer is formally accepted by the seller.',
                    submitCta: 'Complete the pre-authorization',
                },
            },
            related: {
                auctions: 'Related vehicles at auction',
                directSales: 'Related vehicles',
            },
            orderInProgressModal: {
                title: 'We are processing your order',
            },
        },
    },
    asset: {
        type: {
            [AssetType.Bus]: 'Bus | Buses',
        },
        busType: {
            base: 'Type',
            [BusType.Autobus]: 'Autobus',
            [BusType.Minibus]: 'Minibus',
            [BusType.Other]: 'Other',
        },
        class: {
            base: 'Class',
            [PlatformBusClass.City]: 'City',
            [PlatformBusClass.Intercity]: 'Intercity',
            [PlatformBusClass.Touristic]: 'Touristic',
            [PlatformBusClass.SchoolBus]: 'School Bus',
        },
        firstRegistration: 'First registration',
        kilometers: 'Kilometers',
        antiPollution: 'Emission standard (ex antipollution)',
        euroNorm: {
            [AssetEuroNorm.Euro0]: 'Euro 0',
            [AssetEuroNorm.Euro1]: 'Euro 1',
            [AssetEuroNorm.Euro2]: 'Euro 2',
            [AssetEuroNorm.Euro3]: 'Euro 3',
            [AssetEuroNorm.Euro4]: 'Euro 4',
            [AssetEuroNorm.Euro5]: 'Euro 5',
            [AssetEuroNorm.Euro6]: 'Euro 6',
        },
        toBeScrapped: 'To be scrapped',
        expiredInspection: 'Inspection expired',
        bundle: 'Bundle',
        vehicleSpecs: 'Vehicle specifications',
        bundleSpecs: 'Bundle specifications',
        vehiclesInBundle: 'Vehicles included in the bundle',
        vehicleIndex: 'Vehicle #{index}',
        vehicleCount: '{count} vehicle | {count} vehicles',
        generalInformation: 'General information',
        version: 'Version',
        location: 'Location',
        vehicleCondition: {
            base: 'Vehicle condition',
            [VehicleCondition.Blocked]: 'Blocked',
            [VehicleCondition.Rollable]: 'Towable',
            [VehicleCondition.Running]: 'Running',
        },
        registrationDocumentCountry: 'Document nationality',
        gearbox: {
            base: 'Gearbox',
            stateOfUse: {
                [ThreeLevelStateOfUse.Excellent]: 'Gearbox in excellent condition, no difficulty selecting gears and flawless operation',
                [ThreeLevelStateOfUse.Fair]: 'Gearbox in good condition, occasional difficulty selecting gears but still functional',
                [ThreeLevelStateOfUse.Critical]: 'Gearbox in very poor condition; urgent intervention needed due to jams or malfunctions',
            },
            gearboxType: {
                base: 'Transmission type',
                [AssetGearboxType.Auto]: 'Automatic',
                [AssetGearboxType.Manual]: 'Manual',
                [AssetGearboxType.SemiAuto]: 'Semi-automatic',
            },
            brand: 'Transmission brand',
            model: 'Transmission model',
            missing: 'Gearbox missing',
        },
        airConditioner: {
            base: 'Air conditioner',
            stateOfUse: {
                [ThreeLevelStateOfUse.Excellent]: 'Air conditioning in excellent condition and regularly maintained',
                [ThreeLevelStateOfUse.Fair]: 'Air conditioning working but with low effectiveness',
                [ThreeLevelStateOfUse.Critical]: 'Air conditioning not working',
            },
        },
        compressedAirSystem: {
            base: 'Compressed air system',
            stateOfUse: {
                [FiveLevelStateOfUse.Excellent]: 'Suspension working perfectly, no air leaks; all compressed-air-driven services operate flawlessly.',
                [FiveLevelStateOfUse.Good]: 'Compressed air system in very good condition, minimal pressure loss and near-perfect performance.',
                [FiveLevelStateOfUse.Fair]: 'Suspension working, small air leaks, doors operate with some hesitation.',
                [FiveLevelStateOfUse.Poor]: 'Compressed air system in mediocre condition, audible leaks; higher engine revs needed for acceptable operation.',
                [FiveLevelStateOfUse.Critical]: 'Widespread air leaks; services not working.',
            },
        },
        interiors: {
            base: 'Interiors',
            stateOfUse: {
                [FiveLevelStateOfUse.Excellent]: 'Impeccable interiors: seats without stains or tears; floor and trim in perfect condition. All controls operate smoothly.',
                [FiveLevelStateOfUse.Good]: 'Overall good interior condition. Seats with minimal wear, intact flooring, fully functioning instruments.',
                [FiveLevelStateOfUse.Fair]: 'Acceptable for the vehicle’s age: signs of use on seats and floors but no major tears. Minor repairs recommended.',
                [FiveLevelStateOfUse.Poor]: 'Poor interior condition: torn or stained seats, worn trim, partially functioning controls.',
                [FiveLevelStateOfUse.Critical]: 'Critical condition: interiors severely damaged, many worn seats, non-functioning controls, worn or damaged flooring.',
            },
        },
        engine: {
            base: 'Engine',
            stateOfUse: {
                [FiveLevelStateOfUse.Excellent]: 'Engine and mechanics in excellent condition: no leaks, smooth operation, regular maintenance. No intervention needed.',
                [FiveLevelStateOfUse.Good]: 'Reliable mechanics. Engine in good overall condition with minor routine maintenance needs.',
                [FiveLevelStateOfUse.Fair]: 'Acceptable mechanical condition: decent operation with need for repair or minor leaks.',
                [FiveLevelStateOfUse.Poor]: 'Clear mechanical issues: abnormal noises, small leaks, or need for more significant repairs.',
                [FiveLevelStateOfUse.Critical]: 'Engine in critical condition or not working.',
            },
            brand: 'Engine brand',
            model: 'Engine model',
            position: {
                base: 'Engine position',
                [AssetPartPosition.Rear]: 'Rear',
                [AssetPartPosition.Central]: 'Central',
                [AssetPartPosition.Frontal]: 'Frontal',
            },
            missing: 'Engine missing',
        },
        tyres: {
            base: 'Tyres',
            stateOfUse: {
                [FiveLevelStateOfUse.Excellent]: 'Tyres in excellent condition, tread over 80% and even wear. No signs of damage.',
                [FiveLevelStateOfUse.Good]: 'Good condition: tread at 60–80% and light wear. Ready for use with no immediate replacement.',
                [FiveLevelStateOfUse.Fair]: 'Acceptable condition: tread at 40–60%. Short-term replacement recommended for intensive use.',
                [FiveLevelStateOfUse.Poor]: 'Worn tyres: tread below 40%, minor damage or uneven wear. Replacement recommended.',
                [FiveLevelStateOfUse.Critical]: 'Dangerous condition: insufficient tread or evident structural damage. Immediate replacement required.',
            },
        },
        structureBody: {
            base: 'Bodywork',
            stateOfUse: {
                [FiveLevelStateOfUse.Excellent]: 'Bodywork in excellent condition with even, glossy paint. No scratches, dents, or rust. All exterior components intact.',
                [FiveLevelStateOfUse.Good]: 'Bodywork in very good overall condition. Only minor chips or small scratches consistent with age and use. No visible rust.',
                [FiveLevelStateOfUse.Fair]: 'Good condition with signs of normal wear. Some scratches and light dents. Minimal, repairable surface rust.',
                [FiveLevelStateOfUse.Poor]: 'Mediocre bodywork: marked scratches, evident dents, and deeper rust. Repairs required.',
                [FiveLevelStateOfUse.Critical]: 'Critical condition: large dented areas, widespread structural rust, and damaged or missing panels. Significant work required.',
            },
        },
        missingParts: 'Missing parts',
        useType: {
            base: 'Type of Use',
            [AssetUseType.OwnUse]: 'Own',
            [AssetUseType.ThirdPartRent]: 'Third-party rental',
            [AssetUseType.Line]: 'Line',
        },
        length: 'Length',
        height: 'Height',
        width: 'Width',
        doubleDecker: 'Double decker',
        vin: 'V.I.N.',
        converter: 'Converter',
        visitDate: {
            base: 'Viewable',
            fromTo: 'on {date} from {fromTime} to {toTime}',
        },
        inspectionExpiryDate: 'Inspection expiration date',
        doors: {
            base: 'Doors',
            [AssetDoors.Double]: '2',
            [AssetDoors.DoubleDouble]: '2+2',
            [AssetDoors.DoubleDoubleDouble]: '2+2+2',
            [AssetDoors.DoubleDoubleSingle]: '2+2+1',
            [AssetDoors.DoubleSingleSingle]: '2+1+1',
            [AssetDoors.Single]: '1',
            [AssetDoors.SingleDouble]: '1+2',
            [AssetDoors.SingleSingle]: '1+1',
            [AssetDoors.SingleDoubleSingle]: '1+2+1',
            [AssetDoors.SingleSingleSingle]: '1+1+1',
            [AssetDoors.SingleDoubleDouble]: '1+2+2',
            [AssetDoors.DoubleDoubleDoubleDouble]: '2+2+2+2',
            [AssetDoors.DoubleSingle]: '2+1',
        },
        seatUpholstery: {
            base: 'Seat upholstery',
            [AssetSeatUpholstery.Fabric]: 'Fabric',
            [AssetSeatUpholstery.Leather]: 'Leather',
            [AssetSeatUpholstery.Plastic]: 'Plastic',
            [AssetSeatUpholstery.FabricLeather]: 'Fabric Leather',
        },
        seats: 'Seats',
        totalCapacity: 'Total passenger capacity',
        seatsFromS1: "Seats including driver's seat from registration certificate S1",
        standingPlaces: 'Standing places',
        standingPlacesFromS2: 'Standing place from registration certificate S2',
        foldingSeats: 'Folding seats',
        wheelchairPlaces: 'Wheelchair places',
        companionSeats: 'Companion seats',
        wheelchairRamp: {
            base: 'Wheelchair ramp',
            [AssetWheelchairRamp.Absent]: 'Absent',
            [AssetWheelchairRamp.Electric]: 'Electric',
            [AssetWheelchairRamp.Manual]: 'Manual',
            [AssetWheelchairRamp.Electropneumatic]: 'Electro-pneumatic',
        },
        wheelchairRampWorking: 'Wheelchair ramp working',
        luggageCapacity: 'Luggage capacity',
        passThroughLuggageCompartment: 'Pass-through Luggage Compartment',
        hatBox: 'Hat Box',
        glasses: {
            base: 'Windows',
            [Glasses.Single]: 'Single pane',
            [Glasses.Double]: 'Double pane',
        },
        openableWindows: 'Operational windows',
        hp: 'Horsepower',
        kw: 'Power',
        displacement: 'Displacement',
        cylinders: 'Number of cylinders',
        fuel: {
            base: 'Fuel',
            [AssetFuel.CNG]: 'CNG',
            [AssetFuel.Electric]: 'Electric',
            [AssetFuel.GPL]: 'LPG',
            [AssetFuel.Diesel]: 'Diesel',
            [AssetFuel.Gasoline]: 'Gasoline',
            [AssetFuel.Hybrid]: 'Hybrid',
            [AssetFuel.Hydrogen]: 'Hydrogen',
        },
        fuelTankCapacity: 'Fuel tank capacity',
        cngTanks: 'CNG tanks',
        cngTanksMaterial: {
            base: 'Fuel tank material',
            [CngTanksMaterials.Aluminum]: 'Aluminium',
            [CngTanksMaterials.Composite]: 'Composite',
            [CngTanksMaterials.Steel]: 'Steel',
        },
        batteryPower: 'Battery capacity',
        adBlue: 'AdBlue',
        chargingType: {
            base: 'Charging type',
            [ChargingType.BatterySwapping]: 'Battery swapping',
            [ChargingType.Induction]: 'Induction',
            [ChargingType.PantographCharger]: 'Pantograph charger',
            [ChargingType.AC]: 'Alternating current plug in',
            [ChargingType.DC]: 'Direct current plug in',
        },
        wheelSize: 'Wheel size',
        wheels: 'number of wheels (single or double)',
        axles: 'Axles',
        wheelsSizeAxle1: 'Tire size for axle 1',
        wheelsSizeAxle2: 'Tire size for axle 2',
        wheelsSizeAxle3: 'Tire size for axle 3',
        alloyWheels: 'Alloy wheels',
        spareWheel: 'Spare wheel',
        firePreventionSystem: 'Fire alarm system',
        firePreventionSystemBrand: 'Fire alarm system brand',
        abs: 'ABS',
        asr: 'ASR',
        retarder: 'Retarder',
        breathalyzer: 'Breathalyzer',
        tachograph: { base: 'Tachograph', [AssetTachograph.Absent]: 'Absent', [AssetTachograph.Analog]: 'Analog', [AssetTachograph.Digital]: 'Digital' },
        indicatorSigns: 'Destination signs',
        indicatorSignsBrand: 'Destination signs brand',
        accessories: 'Accessories',
        cruiseControl: {
            base: 'Cruise control',
            [CruiseControlType.No]: 'No',
            [CruiseControlType.Adaptive]: 'Adaptive',
            [CruiseControlType.Standard]: 'Standard',
        },
        deckType: {
            base: 'Floor',
            [DeckType.LowEntry]: 'Low Entry',
            [DeckType.LowFloor]: 'Low Floor',
            [DeckType.NormalFloor]: 'Normal Floor',
        },
        acDriver: 'Driver air conditioning',
        driverBerth: 'Driver berth',
        wifi: 'Wi-Fi',
        cctv: 'CCTV',
        internalCameras: 'Internal cameras',
        externalCameras: 'External cameras',
        retrocamera: 'Reversing camera',
        microphone: 'Microphone',
        cdPlayer: 'CD player',
        dvdPlayer: 'DVD player',
        usbPlayer: 'Radio USB player',
        heater: 'Heater',
        preHeater: 'Preheater',
        kitchen: 'Kitchen',
        coffeeMachine: 'Coffee machine',
        refrigerator: 'Refrigerator',
        toilet: 'Toilet',
        safetyBelts: 'Safety belts',
        acPassengers: 'Passengers air conditioning',
        curtains: 'Curtains',
        seatPocket: 'Seat pocket',
        footRest: 'Foot rest',
        handRest: 'Hand rest',
        mp3: 'MP3',
        monitors: 'Monitors',
        seatsMonitor: 'Seats monitor',
        seatsUsbPorts: 'Seats USB port',
        socket220: 'Driver Electric socket',
        seatsSocket220: 'Passenger seat electric socket 220V',
        seatsRadio: 'Seats radio',
        extendableSeats: 'Extendable seats',
        recliningSeats: 'Reclining seats',
        coffeeTables: 'Coffee tables',
        certifiedEngineMaintenance: 'Certified engine maintenance',
        engineMaintenanceMileage: 'Engine maintenance mileage',
        certifiedGearboxMaintenance: 'Certified gearbox maintenance',
        gearboxMaintenanceMileage: 'Gearbox maintenance mileage',
        vehicleRemarks: {
            [VehicleRemark.MILEAGE_UNCERTAIN_TRUST_REPORT]: 'The mileage may be slightly higher than the value indicated in the TrustReport.',
            [VehicleRemark.MILEAGE_UNCERTAIN_LAST_INSPECTION]: 'The mileage data is based on the last inspection carried out and may not be fully accurate.',
            [VehicleRemark.E5EEV_PARTICULATE_CLASSIFICATION]: 'For particulate emissions classification purposes only, the vehicle qualifies as EURO 5.',
            [VehicleRemark.CNG_DUAL_FUEL_SYSTEM]: 'Vehicle with dual fuel system: Petrol / CNG.',
            [VehicleRemark.CNG_CYLINDER_INSPECTION_BUYER_EXPENSE]: "The CNG cylinder inspection is at the buyer's expense.",
            [VehicleRemark.MORTGAGE_EXPIRED_ENFORCEABILITY]: 'The vehicle registration report shows a mortgage with expired enforceability, which does not affect the transfer of ownership.',
            [VehicleRemark.SPAIN_INDIVIDUAL_APPROVAL_PRE_2012]: 'Please note that this vehicle, if purchased for export to Spain, requires individual approval before registration can proceed.',
            [VehicleRemark.ITALY_FOREIGN_DOCS_NATIONALIZATION]: 'Please note that this vehicle’s documents originate from {documentCountry}; therefore, in the event of a sale in Italy, all nationalization and registration procedures shall be the responsibility of the buyer.',
            [VehicleRemark.ITALY_INSPECTION_EXPIRED_EXPORT]: "The vehicle's inspection has expired, and in order to proceed with the export outside of Italy, it is necessary to carry out a new vehicle inspection.",
            [VehicleRemark.DELIVERY_DELAY_LEASING_15_DAYS]: 'Vehicle delivery may be delayed by up to 15 working days compared to the standard schedule.',
            [VehicleRemark.DELIVERY_DELAY_10_DAYS]: 'Vehicle delivery may be delayed by up to 10 working days compared to the standard schedule.',
            [VehicleRemark.DELIVERY_DELAY_20_DAYS]: 'Vehicle delivery may be delayed by up to 20 working days compared to the standard schedule.',
            [VehicleRemark.DELIVERY_DELAY_30_DAYS]: 'Vehicle delivery may be delayed by up to 30 working days compared to the standard schedule.',
            [VehicleRemark.DISMANTLING_TECHNICAL_FLUIDS_PRESENT]: 'The vehicle will be delivered with the technical fluids present (engine oil, gearbox and axle/differential lubricants, coolants, R134A refrigerant gas, and air conditioning compressor lubricant).',
            [VehicleRemark.DISMANTLING_FLUIDS_AND_CNG_PRESENT]: 'The vehicle will be delivered with the technical fluids present (engine oil, gearbox and axle/differential lubricants, coolants, R134A refrigerant gas, and air conditioning compressor lubricant) as well as CNG cylinders containing pressurized gas.',
            [VehicleRemark.BRANDING_OBSCURED_FOR_PRIVACY]: "Please note that the branding has been obscured for the seller's privacy; the vehicle will be delivered with the logo visible.",
        },
    },
    slp: {
        filters: {
            applied: 'Applied Filters',
            inputPlaceholder: 'Select',
            busType: 'Type',
            class: 'Class',
            brand: 'Brand, model, version',
            brandFilter: 'Brand',
            brandFilterAnyOption: 'All brands',
            modelFilter: 'Model',
            modelFilterAnyOption: 'All models',
            versionFilter: 'Version',
            versionFilterAnyOption: 'All versions',
            euroNorm: 'Emission standard (ex antipollution)',
            firstRegistrationYear: 'Registration',
            firstRegistrationYearFrom: 'From (year)',
            firstRegistrationYearTo: 'To (year)',
            firstRegistrationYearAnyOption: 'All years',
            km: 'Kilometers',
            kmAnyOption: 'Any mileage',
            scrapping: {
                title: 'Scrapping restrictions',
                help: 'What are scrapping restrictions?',
                all: 'All vehicles',
                [RequireScrappingFilter.requireScrapping]: 'With scrapping restrictions',
                [RequireScrappingFilter.doNotRequireScrapping]: 'Without scrapping restrictions',
            },
            fuel: 'Fuel',
            length: 'Length',
            lengthAnyOption: 'Any length',
            price: 'Price',
            priceAnyOption: 'All prices',
            seats: 'Seating capacity',
            empty: 'No filters applied.',
            doubleDecker: 'Double decker',
            width: 'Width',
            widthAnyOption: 'Any width',
            height: 'Height',
            heightAnyOption: 'Any height',
            deckType: 'Deck type',
            vehicleCondition: 'Condition',
            useType: 'Use type',
            engineBrand: 'Engine brand',
            enginePosition: 'Engine position',
            fuelTankCapacityFrom: 'Fuel tank capacity (from)',
            fuelTankCapacityTo: 'Fuel tank capacity (to)',
            gearboxBrand: 'Gearbox brand',
            gearboxType: 'Gearbox type',
            chargingType: 'Charging type',
            asr: 'ASR',
            retarder: 'Retarder',
            kilowatts: 'Power',
            kwAnyOption: 'Any power',
            axles: 'Number of axles',
            wheels: 'Number of wheels',
            spareWheel: 'Spare wheel',
            alloyWheels: 'Alloy wheels',
            wheelsSizeAxle1: 'Tyres 1st axle',
            wheelsSizeAxle2: 'Tyres 2nd axle',
            wheelsSizeAxle3: 'Tyres 3rd axle',
            doors: 'Doors',
            totalCapacity: 'Total seats',
            standingPlaces: 'Standing places',
            openableWindows: 'Openable windows',
            displacement: 'Displacement',
            from: 'From',
            to: 'To',
            anyOption: 'Any',
            foldingSeats: 'Folding seats',
            companionSeats: 'Companion seats',
            wheelchairPlaces: 'Wheelchair places',
            wheelchairRamp: 'Wheelchair ramp type',
            passThroughLuggageCompartment: 'Pass-through luggage compartment',
            seatUpholstery: 'Seat upholstery',
            tachograph: 'Tachograph',
            cruiseControl: 'Cruise control',
            glasses: 'Windows',
            monitors: 'Monitors',
            driverBerth: 'Driver berth',
            socket220: '220V socket (Driver seat)',
            microphone: 'Microphone',
            toilet: 'Toilet/WC',
            preHeater: 'Pre-heater',
            saleType: {
                base: 'Vehicle lot',
                [SaleBundleType.Mono]: 'No',
                [SaleBundleType.BundleMonoVersion]: 'Lot of identical vehicles',
                [SaleBundleType.BundleMultiVersion]: 'Lot of different vehicles',
            },
            countryCodes: 'Country of location',
            registrationDocumentCountry: 'Registration country',
            acDriver: 'Driver air conditioning',
            acPassengers: 'Passenger air conditioning',
            heater: 'Heater',
            abs: 'ABS',
            breathalyzer: 'Breathalyzer',
            cctv: 'CCTV',
            internalCameras: 'Internal cameras',
            externalCameras: 'External cameras',
            retrocamera: 'Rear camera',
            safetyBelts: 'Safety belts',
            indicatorSigns: 'Indicator signs',
            firePreventionSystem: 'Fire prevention system',
            wifi: 'WIFI',
            mp3: 'MP3',
            cdPlayer: 'CD player',
            dvdPlayer: 'DVD player',
            usbPlayer: 'USB player',
            kitchen: 'Kitchen',
            coffeeMachine: 'Coffee machine',
            refrigerator: 'Refrigerator',
            handRest: 'Armrests',
            footRest: 'Footrests',
            hatBox: 'Luggage rack',
            seatPocket: 'Seat pockets',
            coffeeTables: 'Tables',
            extendableSeats: 'Extendable seats',
            recliningSeats: 'Reclining seats',
            curtains: 'Curtains',
            seatsSocket220: '220V seat sockets',
            certifiedEngineMaintenance: 'Certified engine maintenance',
            certifiedGearboxMaintenance: 'Certified gearbox maintenance',
            enginePresent: 'Engine present',
            gearboxPresent: 'Gearbox present',
            seatsMonitor: 'Seat monitors',
            seatsRadio: 'Seat radios',
            seatsUsbPorts: 'Seat USB ports',
            advancedModal: {
                title: 'Advanced filters',
                back: 'Back to results',
                cancelAll: 'Clear all filters',
                show: 'Show {count} vehicle | Show {count} vehicles',
                contact: '0 vehicles found - Contact us',
            },
            advanced: {
                sectionType: {
                    title: 'Listing type',
                },
                sectionVehicleData: {
                    title: 'Vehicle data',
                    vehicleCondition: 'Vehicle condition',
                },
                sectionSpecificationsAndEquipment: {
                    title: 'Specifications and equipment',
                    mechanicsAndTransmission: 'Mechanics and transmission',
                    axlesAndTyres: 'Axles and tyres',
                    tyreSizePerAxle: 'Tyre size per axle',
                    totalCapacity: 'Total seats',
                    accessoriesAndComfort: 'Accessories and comfort',
                    onBoardServices: 'On-board services',
                    connectivityAndMedia: 'Connectivity and media',
                    passengersComfort: 'Passenger comfort',
                    safetyDevices: 'Safety devices',
                    ac: 'Air conditioning',
                    videocameras: 'Video cameras',
                },
            },
        },
        appliedFilters: {
            from: '{0} from: {1}',
            to: '{0} to: {1}',
            seatsFrom: 'from: {0} seats',
            seatsTo: 'to: {0} seats',
            reset: 'Reset filters',
            appliedFiltersCounter: '{count} active filter | {count} active filters',
            standingPlacesFrom: 'from: {0} standing places',
            standingPlacesTo: 'to: {0} standing places',
        },
        sorting: {
            title: 'Sort by',
            yearAsc: 'Oldest registration',
            yearDesc: 'Most recent registration',
            priceAsc: 'Lowest price',
            priceDesc: 'Highest price',
            kmAsc: 'Lowest mileage',
            kmDesc: 'Highest mileage',
            startAtAsc: 'Oldest listing',
            startAtDesc: 'Newest listing',
            default: 'Featured vehicles',
        },
        navigation: {
            auction: 'Auctions',
            directSale: 'Buy Now',
        },
        page: {
            results: '{total} visible vehicles',
            resetFilters: 'Remove filters',
            related: {
                base: '{total} vehicles corresponding to the same filters in {link}',
                link: {
                    [SellingMethod.Auction]: 'Auction',
                    [SellingMethod.DirectSale]: 'Buy Now',
                },
            },
        },
        [SellingMethod.Auction]: {
            page: {
                title: {
                    base: 'Discover all the Buses at Auction on Fleequid',
                    filtered: '{assetType} {filters} at Auction',
                },
                emptyStateTitle: 'No results',
                emptyStateText: 'The applied filters did not produce any results.',
                emptyStateTitleRunningFilterOnly: 'The next auctions are being scheduled. Check back later.',
                emptyStateTextRunningFilterOnly: 'You can still visit the {0} section to find your vehicle now.',
            },
            filters: {
                state: {
                    title: 'Auction state',
                    [AuctionState.Pending]: 'Upcoming auctions',
                    [AuctionState.Running]: 'Live auctions',
                    [AuctionState.Expired]: 'Closed auctions',
                },
            },
            sorting: {
                defaultByState: {
                    all: 'Default order',
                    [AuctionState.Pending]: 'Soonest start',
                    [AuctionState.Running]: 'Soonest end',
                    [AuctionState.Expired]: 'Most recent end',
                },
            },
        },
        [SellingMethod.DirectSale]: {
            page: {
                title: {
                    base: "Discover all the Buses in Fleequid's Buy Now",
                    filtered: '{assetType} {filters} in Buy Now',
                },
                emptyStateTitle: 'No results',
                emptyStateText: 'The applied filters did not produce any results.',
            },
            filters: {
                state: {
                    title: 'Sale state',
                    [DirectSaleState.Open]: 'Running sales',
                    [DirectSaleState.Closed]: 'Closed sales',
                },
            },
            negotiationAvailableBadge: {
                title: 'Negotiation available',
            },
        },
    },
    auction: {
        reservePrice: {
            notSet: {
                base: '{0} reserve price',
                highlight: 'No',
            },
            notReached: {
                base: 'Reserve price {0}',
                highlight: 'not met',
            },
            reached: {
                base: 'Reserve price {0}',
                highlight: 'met',
            },
            tooltip: {
                notSet: 'No reserve price set: the vehicle will automatically be awarded to the highest bidder',
                reached: 'The reserve price has been met, the vehicle will be awarded to the highest bidder',
                notReached: 'The reserve price has not yet been met',
            },
        },
    },
    order: {
        state: {
            [OrderState.Processing]: 'Processing',
            [OrderState.Handled]: 'Handled',
            [OrderState.Paid]: 'Paid',
            [OrderState.Canceled]: 'Canceled',
            [OrderState.NotPaid]: 'Unpaid',
        },
        edit: {
            title: 'Configure your order',
            steps: {
                services: 'Services',
                payment: 'Payment',
            },
            addToOrder: 'Add to your order',
            exit: 'Exit',
            skip: 'Skip',
            apiError: 'An error occurred while purchasing additional services, please try again later or contact support',
            paymentStatusActionButtonTrigger: 'Show order details',
            bottomSheetTitle: 'Order details',
            modal: {
                noSelection: {
                    title: 'You have not selected any service',
                    description: 'Are you sure you want to proceed without added services?',
                    confirm: 'Proceed without added services',
                    cancel: 'Go back',
                },
                noAdded: {
                    title: 'You selected a service, but did not add it to the order',
                    description1: 'To add it to your order, click on “{0}”.',
                    description2: 'Alternatively, you can cancel and review the selection.',
                    confirm: 'Add to your order',
                    cancel: 'Exit',
                },
                skipNoAdded: {
                    title: 'You selected a service, but did not add it to the order',
                    description1: 'To add it to your order, click on “{0}”.',
                    description2: 'Alternatively, you can cancel and review the selection.',
                    confirm: 'Add to your order',
                    cancel: 'Proceed without added services',
                },
            },
        },
        paymentStatus: {
            title: 'Summary',
            item: {
                vehicle: 'Vehicle | Vehicles',
                price: 'Price',
                fee: {
                    base: 'Fees',
                    link: 'What are these?',
                },
                services: 'Additional services',
                netTotal: 'Total',
            },
        },
        item: {
            state: {
                unavailable: 'Unavailable',
                toBeConfirmed: 'To be confirmed',
            },
            dependencyRequired: `{deps} required`,
            countdown: '{days} days remaining',
        },
        validation: {
            minimumOrder: 'At least one service must be selected',
            consent: 'Consent',
        },
        card: {
            summary: {
                title: 'Order Summary',
                order: 'Order: {incrementId}',
                createdAt: 'Date: {date}',
            },
            invoiceInformations: {
                title: 'Billing Information',
            },
            sale: {
                title: 'Vehicle',
                vin: 'VIN: {vin}',
            },
            paymentMethod: {
                title: 'Payment Method',
                bankTransfer: {
                    title: 'Bank Transfer',
                    description: 'Pay within three days of purchase confirmation',
                    extension1: 'We will send you the complete bank details directly to your email inbox as soon as the calculation of the fees is finalized.',
                    extension2: 'Fleequid service fees and charges, if applicable, are not included in the subtotal. You will receive the details by email after purchase confirmation.',
                    fee: {
                        base: 'Fees ({link})',
                        link: 'What are these?',
                    },
                },
                bail: {
                    title: 'Security deposit',
                    user: {
                        who: 'Paid by: {name}',
                        when: 'On {date}',
                    },
                    status: {
                        base: 'Pending',
                        [PaymentProviderActionResult.Voided]: 'Voided',
                        [PaymentProviderActionResult.Captured]: 'Captured',
                    },
                },
            },
            vas: {
                title: 'Additional services',
                add: 'Add',
                destinationAddress: 'Delivery to: {address}',
                empty: 'No service added',
            },
            paymentStatus: {
                title: 'Payment status',
            },
        },
        thankYou: {
            hero: {
                title: 'Order received',
                subtitle: 'Order: {incrementId} successfully received!',
                cta: 'Go to your orders',
                info1: 'Dear {company}, we have successfully recorded your order.',
                info2: 'A confirmation has been sent to {email}.',
                info3: 'Below you can find the complete summary of your purchase.',
            },
            whatsHappening: {
                title: 'What happens now?',
                item: {
                    step1: 'Fleequid will send you the invoice to the specified address.',
                    step2: 'You have 3 days to complete the payment.',
                    step3: 'Upon payment confirmation, Fleequid will release the deposit.',
                    step4: 'Fleequid will initiate the documents and the transfer of ownership.',
                },
            },
            buy: 'Continue shopping',
            support: {
                title: 'Do you have any doubts or questions?',
            },
        },
        listItem: {
            sellingMethod: {
                [SellingMethod.DirectSale]: 'Buy Now',
                [SellingMethod.Auction]: 'Auction',
            },
            createdAt: 'Placed on:',
            order: 'Order:',
        },
    },
    vas: {
        services: {
            title: 'Additional services',
        },
        [ValueAddedServiceCode.LogisticsService]: {
            title: 'Secure delivery',
            subtitle: 'We will transport and deliver your vehicle to the chosen address',
            destination: 'Destination city and country',
            calculate: 'Calculate',
            edit: 'Edit',
            calculatedExpenses: 'Calculated expenses',
            transportTermsConsent: {
                base: 'I have read and accept the {link}',
                link: 'Specific Conditions - Fleequid Transport Service',
            },
            vexatiousClauses: 'I have read and accept the following clauses:',
            validation: {
                invalidDestination: 'The delivery service is not available for this destination',
                mandatoryConsent: 'Mandatory consent',
                apiError: 'The delivery service is currently not available',
            },
        },
        [ValueAddedServiceCode.ProvisionalLicensePlate]: {
            title: 'Temporary license plate',
            subtitle: 'Issuance of a temporary plate pending registration',
        },
        [ValueAddedServiceCode.VehicleNationalization]: {
            title: 'Nationalization',
            subtitle: 'Compliance for circulation on Italian territory',
        },
        [ValueAddedServiceCode.VehicleRegistration]: {
            title: 'Registration',
            subtitle: 'Get the permanent plate for your vehicle',
        },
        [ValueAddedServiceCode.PowertrainWarranty]: {
            highlightedTitle: {
                base: '{highlight} Warranty',
                highlight: 'Powertrain',
            },
            title: 'Powertrain Warranty',
            subtitle: 'Choose from 3 to 12 months of additional warranty covering potential damage to the engine and transmission (excluding those already reported in the TrustReport). Coverage up to €25,000.',
            duration: {
                label: 'Warranty duration',
                itemLabel: '{months} months / {km}',
                placeholder: 'Select your preferred duration',
            },
            termsConsent: {
                base: 'I have read and accept the {link}',
                link: 'Specific conditions – Powertrain',
            },
            vexatiousClauses: 'I have read and accept the following clauses:',
            validation: {
                mandatoryConsent: 'Mandatory consent',
            },
        },
        [ValueAddedServiceCode.FleequidCare]: {
            highlightedTitle: {
                base: '{highlight} Warranty',
                highlight: 'Fleequid Care',
            },
            title: 'Fleequid Care Warranty',
            subtitle: 'Coverage for pre-existing and undisclosed engine and transmission defects',
            content: {
                base: "What's included:",
                item1: 'Engine and transmission fault diagnosis and repair',
                item2: 'Service at Fleequid Authorized Centers',
                item3: 'Compensation up to €10,000',
                item4: {
                    base: 'Part of the compensation as a Fleequid Voucher (min. 30%) {caption}',
                    caption: 'Can be used for commissions on future purchases/sales',
                },
                coverApplication: 'After delivery, any engine and transmission failures must be reported within 5 days and handled through authorized centers. {br} Coverage does not apply to wear and tear, inadequate maintenance, or unauthorized repairs.',
            },
            termsConsent: {
                base: 'I have read and accept the {link}',
                link: 'Specific conditions – Fleequid Care',
            },
            vexatiousClauses: 'I have read and accept the following clauses:',
            validation: {
                mandatoryConsent: 'Mandatory consent',
            },
        },
        emptyState: 'No additional services are available for this order',
    },
    homepage: {
        link: 'Home',
        hero: {
            title: 'The largest verified fleet in Europe',
        },
        why: {
            title: 'Why Fleequid?',
            section: {
                purchase: 'Search through hundreds of buses worldwide and buy your next vehicle in 7 days.',
                assistance: 'We help you choose with detailed images, complete information, and high-quality technical support.',
                fairness: 'The bus is sold at its fair price thanks to the auction mechanism.',
            },
        },
        howItWorks: {
            title: 'How does the auction work?',
            section: {
                register: {
                    title: '1. Register your company',
                    content: 'Enter your company details on the platform.',
                },
                choose: {
                    title: '2. Choose your bus',
                    content: 'Browse ongoing and upcoming auctions to find your dream vehicle. Use filters, images, and detailed specifications to help you.',
                },
                bid: {
                    title: '3. Place your bid',
                    content: 'When the auction is live, join in! Follow the bidding progress and bid again if yours is outbid. Remember: we need to ensure your bid is serious, so add a deposit with a credit card to validate it.',
                },
                autobid: {
                    title: '4. Try automatic bidding',
                    content: 'Afraid of losing the auction? Set a maximum bid you are willing to offer, and we will bid automatically on your behalf to outbid other users (within your limit).',
                },
                closure: {
                    title: '5. End of the auction',
                    content: "Did you win? Make the payment to Fleequid, which acts as a trusted intermediary: we handle payments and coordinate all administrative procedures during the vehicle's ownership transfer. Didn’t win? Your deposit will be refunded.",
                },
            },
        },
    },
    block: {
        stats: {
            title: 'Fleequid in numbers',
            purchase: 'Vehicles sold',
            markets: 'Countries with registered users',
            team: 'Team members',
            registrations: 'Companies registered from across Europe, North Africa, and the Middle East',
        },
        support: {
            title: 'We are fully at your disposal',
            contacts: 'Do you need support?',
            whatsapp: 'Write to us directly',
        },
        whoWeAre: {
            title: {
                base: 'We have {0} of experience',
                highlight: '40+ years',
            },
            description: "Fleequid was born from the experience of Basco, a leading company in the international market for buying and selling used buses. The speed, pragmatism, and professionalism of Basco were the spark for Fleequid's technological innovation.",
        },
    },
    faq: {
        title: 'Frequently Asked Questions',
        start: {
            base: 'How to get started',
            participate: {
                q: 'How can I participate in an online auction?',
                a: 'To join online auctions on Fleequid you need to sign up at {0} providing your business information and sending the required documents. Once you’ve done that, you can check out the pages with the ongoing auctions, and place one or more bids to compete for the purchase of a vehicle. Auctions are held electronically, and you can place bids until the auction closes. The auction ends when the user who submitted the highest bid awards the vehicle, as long as the bid is at least the reserve price, or if the seller accepts a bid that is lower than the reserve price. ',
                link: 'https://fleequid.com/en/signup',
            },
            reservePrice: {
                q: 'What is the reserve price?',
                a: 'The reserve price is the minimum amount that must be exceeded for the auction to be successful. If this amount is not reached during the auction, it’s up to the seller to decide whether they are willing to accept the best offer they received anyway. The reserve price is a higher amount than the starting bid: it is set by the seller before the start of the auction and will not be visible to the public, who only knows the highest bid made up to that moment.',
            },
            countries: {
                q: 'In which countries is it possible to buy and sell buses through Fleequid?',
                a: 'Fleequid operates in Italy, in the European Union and in several non-EU countries, complying with the regulations of each nation. If you have any doubts about the possibility of using Fleequid from your country, please contact our customer service at {0}.',
            },
            bureaucracy: {
                q: 'Who is responsible for property transfers, vehicle registration and every documentation aspect?',
                a: "Fleequid will communicate to seller and buyer how to handle documentation aspects and payments related to the property transfer, depending on the specifics of the deal (between Italian companies, EU, and non-EU) and on the platform's role in the sales process. However, registration of the vehicle in the country of destination is the responsibility of the buyer.",
            },
            toBeScrapped: {
                q: 'What are vehicles intended for scrapping?',
                a: 'Vehicles intended for scrapping are those which, being no longer suitable for circulation and the transportation of people, must necessarily, according to current laws or regulation rules, undergo a recovery and partial reuse process of their components as spare parts. Participation in auctions for these vehicles is only permitted for dismantlers listed in the waste management register in category 5 and managers of the C.E.R. code 16.01.04. At the moment, the auctions for vehicles intended for scrapping are only available in Italy.',
            },
            vies: {
                q: 'What is Vies? Why is it mandatory to be included?',
                a: 'It’s a search engine (not a database) created by the European Commission to facilitate sales between companies in UE. It’s an electronic mean of validating VAT-identification stats of economic operators registered in the European Union for cross border transactions on goods or services. For this reason, European companies that want to be on Fleequid must necessarily be present there.',
            },
            coc: {
                q: 'What is COC?',
                a: "The COC, or Certificate of Conformity, is a document that verifies a vehicle's compliance with specific technical and safety standards set forth by European regulations. This document includes various information related to the vehicle, including its technical specifications, chassis number, emissions data, approval class and the safety standards the vehicle complies with. It’s required to register a vehicle in a different country within the European Union, as possessing this document avoids the need for further technical checks. The lack of the COC at the time of sale to another country could prevent or make vehicle registration more difficult.",
            },
            buyNowWhat: {
                q: 'What is the "Buy Now" feature?',
                a: 'The “Buy Now” function allows you to immediately purchase a bus at a fixed and non-negotiable price. This mode guarantees a direct and definitive purchase.',
            },
            buyNowHow: {
                q: 'How do I use the "Buy Now" feature?',
                a: 'To use this purchase method, choose a vehicle in the "Buy Now" section. Click on the "Buy Now" button and enter your credit card details as a guarantee to confirm your purchase. After confirmation, you will receive all the instructions from Fleequid to proceed with the collection of the vehicle.',
            },
            buyNowHowToBuy: {
                q: 'How does purchasing in “Buy Now” work?',
                a: 'Enter your credit card details as a guarantee. After confirmation, you will receive instructions from Fleequid for collecting the vehicle.',
            },
            buyNowNegotiate: {
                q: 'Can you negotiate in “Buy Now”?',
                a: 'Yes, negotiation is possible: you can propose an alternative offer, as long as it is not lower than the minimum threshold compared to the listed price.',
            },
            buyNowWhoCanNegotiate: {
                q: 'Who can participate in a negotiation in “Buy Now”?',
                a: 'Multiple buyers can make offers: the seller may receive several simultaneous negotiations. If they decide to accept an offer, they must choose the highest one.',
            },
        },
        sell: {
            base: 'How to sell',
            selling: {
                q: 'How can I start selling on Fleequid?',
                a: 'If you have a bus to sell, please contact our customer service by writing to {0} or fill out the form found at {1}, providing information about the vehicle you want to sell.  You also need to register your company as a seller on the platform.',
            },
            startingPrice: {
                q: 'How is the starting price defined?',
                a: 'The starting price is the minimum price for the vehicle above which potential buyers can place their bids or make counteroffers on the Marketplace. The starting bid is calculated by Fleequid based on its technical assessment of the vehicle and must be lower than the reserve price, which is instead set by the seller.',
            },
            delivery: {
                q: 'How is the vehicle delivered?',
                a: 'After receiving payment, the seller must enable the buyer to collect the vehicle; the buyer must promptly proceed with the collection, which must take place within ten days from the date Fleequid informs the buyer of the completion of the de-registration procedures in the country of origin. For each day of delay beyond the specified timeframe, Fleequid will apply a penalty.',
            },
            howLong: {
                q: 'How long does it take for the bus to be sold?',
                a: 'When a vehicle is uploaded to the platform, Fleequid shares with the seller the start and end dates of the auction and the information about pre-auction visibility; these dates are established by Fleequid based on the specifics of the vehicle to be sold. If the sale did not conclude successfully, Fleequid would still have the right to hold up to 3 consecutive auctions within a total of 60 days.',
            },
            expiredInspection: {
                q: 'Is it possible to buy a vehicle with an expired inspection?',
                a: 'In Italy, to export a vehicle abroad, it must be canceled from the Public Automotive Registry (PRA). One of the conditions for cancellation is that the vehicle has a valid inspection. An uninspected vehicle limits its marketability to Italian territory only. If you want to get more offers and, consequently, a higher selling price, it is advisable for the seller to auction the vehicle with the valid inspection.',
            },
        },
        buy: {
            base: 'How to buy',
            inspection: {
                q: 'Is it possible to inspect vehicles before bidding?',
                a: "The seller can decide whether to offer the option of live inspection of the vehicle. If allowed, the seller will define a specific day for live visits and inspections of the vehicle, directly at the seller's location.",
            },
            claims: {
                q: 'How do you handle hidden defects and complaints?',
                a: 'Fleequid acts as an intermediary between seller and buyer and, as such, is not responsible for any faults, whether hidden or visible, defects, wear and tear, irregularities, unsuitability for use, as well as errors or inaccuracies in the declarations made by the seller. By posting their auction on Fleequid, the seller commits to transparency and to providing accurate information about the condition of the vehicle; at the same time, the buyer must commit to reporting any discrepancies in a timely manner (3 days for apparent defects) within one month from the pick-up for hidden defects of the vehicle. Fleequid is available to help buyers manage complaints and provides s-pecific conditions for their handling.',
            },
            paymentMethods: {
                q: 'What payment methods are accepted?',
                a: 'Fleequid accepts bank transfers, credit card payments, PayPal, Apple Pay and Google Pay. The security deposit can only be paid by credit card, PayPal, Apple Pay or Google Pay. Payment for the vehicle, fees and any additional costs must be made by bank transfer.',
            },
            howPaymentWorks: {
                q: 'How does payment work?',
                a: 'The user who wins the auction is required to pay an amount that includes the winning price, the selling fee for Fleequid and any other agreed-upon fees (see General Conditions for more details). The buyer must pay the amount due by bank transfer no later than three days after receiving the corresponding invoice. Partial payments are not allowed.',
            },
            deposit: {
                q: 'How does the security deposit work?',
                a: 'The security deposit is the sum of money that the user gives to Fleequid when placing a bid in the auction, as a guarantee of the seriousness of the offer itself. When the auction ends all deposits are released, except those of the first and second place winners. If the winner finalizes the purchase of the vehicle, by making the bank transfer for the agreed amount (no later than 3 days after receiving the corresponding invoice), both their deposit and that of the second bidder will be released. If the winner fails to make payment within the agreed time, they will lose both the bus and the security deposit. In this case, the second-place winner will automatically become the new winner of the auction and, by purchasing the vehicle by bank transfer, the security deposit will be returned.',
            },
            autoBid: {
                q: 'How does the Auto Bid work?',
                a1: "The Auto Bid allows you to set a maximum amount you're willing to offer for the auction (excluding fees).",
                a2: "When you activate it, the system immediately places the minimum valid bid, considering the reserve price and the current bids from other users. Subsequently, if other users place bids, the system will automatically outbid them on your behalf, increasing the bid up to the maximum amount you've set, after which the Auto Bid will deactivate.",
                a3: 'The Auto Bid also attempts to reach the reserve price to maximize your chances of winning the auction.',
                a4: 'If multiple users activate the Auto Bid with the same maximum amount, a bid of that maximum amount will be placed for each user, but only the one who activated it first will be considered the winner. Remember that all placed bids are binding, even if they are not winning.',
                a5: 'Please note that you cannot cancel an active Auto Bid, but you can only increase its maximum amount.',
            },
            auctionNegotiation: {
                q: 'How does closed-auction negotiation work?',
                a1: 'Closed-auction negotiation allows the buyer and seller to reach an agreement when an auction closes without the highest bid having reached the reserve price. This process aims to incentivize an agreement between the parties, thereby increasing the chances of a sale.',
                a2: 'The negotiation starts automatically once the auction closes below the reserve price and lasts for up to 72 hours.',
                a3: "When the negotiation begins, both the buyer and the seller can see each other’s proposals, known respectively as 'offer' and 'ask':",
                a3_1: "The 'offer' is the price (net of {0}) that the buyer is willing to pay to secure the negotiation. The buyer’s initial offer to the seller is the highest bid that was placed during the auction.",
                a3_2: "The 'ask' is the lowest price (net of {0}) that the seller is willing to accept to conclude the negotiation. The seller’s initial ask to the buyer is the auction’s reserve price.",
                a4: 'During the negotiation, at any time, both the seller and the buyer can:',
                a4_1: 'Change their own proposal, with the seller lowering the ask and the buyer raising the offer.',
                a4_2: 'Accept the other party’s proposal.',
                a5: 'Additionally, at any moment:',
                a5_1: 'The buyer can lock in their current offer. This action is irreversible and, afterwards, the seller may choose to accept or reject it, thereby concluding the negotiation.',
                a5_2: 'The seller can end the negotiation, causing the auction to close without a final award.',
                a6: 'The negotiation concludes successfully if one of the parties accepts the other party’s proposal. If the negotiation does not result in an acceptance within 72 hours, or if the seller ends it, the auction is considered unsold.',
                fee: 'fees',
            },
        },
        valueAddedServices: {
            base: 'Additional Services',
            additionalServices: {
                q: 'What are "additional services"?',
                a: '"Additional services" are extra options, offered by Fleequid or external partners, that can be added to the purchase of a vehicle to make the buying experience simpler, more complete and personalized.',
            },
            availableServices: {
                q: 'What additional services does Fleequid offer?',
                a1: 'Fleequid currently offers the following additional services:',
                a2: 'Temporary license plate: provision of a temporary plate to export the vehicle without having to register it in Italy.',
                a3: 'Nationalization: registration in Italy of a vehicle from abroad, with verification of documentation and update of registrations.',
                a4: 'Registration: assignment of the license plate and issuance of the circulation certificate that enable the vehicle to circulate in Italy.',
                a5: "Secure delivery: shipment of the vehicle directly to destination. If you do not choose this service, vehicle collection remains the buyer's responsibility.",
            },
            howToRequest: {
                q: 'How can I request an additional service?',
                a1: 'You can add additional services from the order configuration page, accessible in several ways:',
                a2: 'Immediately after completing a Buy Now purchase, you will be automatically redirected to the configuration page.',
                a3: 'From the "Your Account" menu, in the "Your Orders" section, by selecting the desired order.',
                a4: 'Through the link in the vehicle purchase confirmation email.',
            },
            whenToAdd: {
                q: 'When is it possible to add a service?',
                a1: 'Additional services can be requested only after vehicle purchase confirmation and are available within specific time limits, which vary depending on the type of service:',
                a2: 'Temporary license plate: available only for Italian vehicles with valid inspection for at least 30 days from the purchase date. Can be added within 40 days of purchase.',
                a3: 'Nationalization: available for vehicles registered abroad and buyers belonging to Italian companies. Can be added within 15 days of purchase.',
                a4: 'Registration: available only if Nationalization has already been added. Can be added within 15 days of the Nationalization addition date.',
                a5: 'Secure delivery: available in all countries, except Kosovo, Bosnia and Serbia (both as starting point and arrival). Can be added within 40 days of the purchase date.',
            },
            costs: {
                q: 'Do additional services have separate costs?',
                a: 'Yes. Each additional service has a specific cost, which is added to the vehicle purchase price.',
            },
            paymentManagement: {
                q: 'How is payment for additional services managed?',
                a: 'If services are added before Fleequid issues the purchase invoice, they will be included in the same vehicle invoice. If added later, they will be invoiced separately.',
            },
            statusTracking: {
                q: 'Where can I see the status of requested additional services?',
                a: 'You can monitor the status of additional services on the order summary page, accessible from the "Your Account" section.',
            },
            unavailableService: {
                q: 'What happens if an additional service is not available?',
                a: 'If an additional service is not available for a specific vehicle, it will be shown as disabled and cannot be added to the order.',
            },
        },
        contactUs: 'We hope you have found all the answers to your questions on this page, but if not, we are always happy to help. Call us on {0}, or contact us by writing to {1}.',
    },
    footer: {
        cookiePolicy: {
            base: 'Cookie policy',
            changeConsent: 'Change your consent',
            revokeConsent: 'Revoke your consent',
        },
        explore: 'Explore',
        legal: 'Legal pages',
        support: 'Do you need support?',
        vat: 'VAT Number',
        backTop: 'Back to top',
        accessibilityStatement: 'Accessibility Statement',
        nextGenEU: 'Funded BY NextGen EU',
        partners: 'We are partners of',
    },
    whatsapp: 'I was visiting {url} and I have some questions:',
    copyright: {
        allRightsReserved: 'All Rights Reserved',
    },
    menu: {
        directSale: 'Buy Now',
        auction: 'Auctions',
        sell: 'Sell',
        howTo: {
            base: 'How to',
            howSellUsedBus: 'Selling a used bus',
            reasonsBuyBusAuction: 'Reasons to buy a bus at auction',
            guideBuyUsedBus: 'Guide to buying a used bus',
            trustReport: "Fleequid's TrustReport",
        },
        faq: 'F.A.Q.',
    },
    gateway: {
        stripeTitle: 'Payments securely processed by Stripe:',
        stripeDescription: 'Your card data does not pass through our servers.',
    },
    mktg: {
        whyFleequid: {
            title: 'Why Fleequid?',
            deal: {
                title: 'Easy deals',
                description: 'A tech platform instead of negotiations with undecided interlocutors',
            },
            processes: {
                title: 'Fast processes',
                description: 'Fleequid saves time and energy for your team',
            },
            support: {
                title: 'A transparent support',
                description: 'From registration to the delivery of the vehicle, everything is clear',
            },
        },
        trustReport: {
            title: {
                bold: 'The Fleequid {highlight}:',
                highlight: 'TrustReport',
                base: 'everything you need to choose your next vehicle.',
            },
            description1: "Every vehicle listed on Fleequid is inspected by one of our technicians directly at the seller's location.",
            description2: 'Following a standard procedure, we take photos and videos and compile the detailed report of the vehicle, then the full TrustReport is available on the listing page',
            conclusion: "It's like being there in person, but with the convenience of staying at your desk.",
            cta: "Discover Fleequid's TrustReport",
        },
    },
    notifications: {
        success: 'Success',
        warning: 'Warning',
        error: 'Error',
        genericError: 'Something went wrong. Please try again later.',
        tryAgain: 'Please try again',
        userOutBid: {
            title: 'Your bid has been outbid!',
            description: 'Your bid of {prevBidAmount} for {subject} has been outbid. The new current price is {currentBidAmount}.',
            titleAutoBidTie: 'Your bid has been beaten!',
            descriptionAutoBidTie: 'Your bid of {prevBidAmount} for {subject} has been beaten because another user had previously placed an auto bid with the same amount.',
            titleAutoBid: 'Your bid has been outbid!',
            descriptionAutoBid: 'Your bid of {prevBidAmount} for {subject} has been outbid because another user had previously placed an auto bid with a higher offer than yours. The new current price is {currentBidAmount}.',
            inAuction: 'this auction',
            linkedAuction: 'the auction of {brand} {model} {version}',
        },
        userBailAuthorized: {
            title: 'You are authorized to participate in the auction!',
            description: 'You have been authorized to participate {subject}',
            inAuction: 'in this auction',
            linkedAuction: 'in the auction of {brand} {model} {version}',
        },
        userMaxBidderRestored: {
            title: 'You are the highest bidder!',
            description: 'Your bid of {prevBidAmount} for {subject} is the highest!',
            inAuction: 'this auction',
            linkedAuction: 'the auction of {brand} {model} {version}',
        },
        auctionWon: {
            title: 'Auction ended - You won!',
            description: 'We are very pleased to inform you that you have won {subject} thanks to your bid of {currentPrice}.',
            titleReservePriceNotMet: 'Auction ended – Your Offer was the highest!',
            descriptionReservePriceNotMet: 'Your offer of {currentPrice} for {subject} was the highest. Unfortunately, the reserve price was not met. A negotiation will begin shortly, and you and the seller can try to reach an agreement. You will have the opportunity to increase your offer, and the seller will be able to revise their ask.',
            inAuction: 'this auction',
            linkedAuction: 'the auction of {brand} {model} {version}',
        },
        userWinningBid: {
            title: 'Your bid is the highest!',
            description: 'Your bid of {amount} for {subject} has been placed, and you are the highest bidder.',
            inAuction: 'this auction',
            linkedAuction: 'the auction of {brand} {model} {version}',
        },
        userAutoBidMaxThresholdReached: {
            title: 'Your Auto Bid has reached its limit!',
            description: 'Your Auto Bid of {amount} for {subject} has reached the maximum limit and is now no longer active.',
            inAuction: 'this auction',
            linkedAuction: 'the auction of {brand} {model} {version}',
        },
        negotiationStarted: {
            title: 'Negotiation started!',
            description: 'Negotiation has started for {subject}.',
            inAuction: 'this auction',
            linkedAuction: 'the auction of {brand} {model} {version}',
        },
        negotiationExpired: {
            title: 'Negotiation expired!',
            description: 'The time for the {subject} has expired. Negotiation ended without success.',
            inAuction: 'negotiation',
            linkedAuction: 'negotiation for {brand} {model} {version}',
        },
        negotiationProposalByBuyer: {
            title: 'Offer changed!',
            description: 'The buyer has changed his offer to {proposalAmount} for the {subject}.',
            inAuction: 'negotiation',
            linkedAuction: 'negotiation for {brand} {model} {version}',
        },
        negotiationProposalBySeller: {
            title: 'Ask changed!',
            description: 'The seller has changed his ask to {proposalAmount} for the {subject}.',
            inAuction: 'negotiation',
            linkedAuction: 'negotiation for {brand} {model} {version}',
        },
        negotiationAcceptedByBuyer: {
            title: 'Ask accepted!',
            description: 'The buyer has accepted your ask of {acceptedAmount} for the {subject}. Negotiation successfully concluded.',
            inAuction: 'negotiation',
            linkedAuction: 'negotiation for {brand} {model} {version}',
        },
        negotiationAcceptedByBackOffice: {
            title: 'Request accepted!',
            description: 'The {subject} has been successfully concluded at the amount of {acceptedAmount}, congratulations.',
            inAuction: 'auction',
            linkedAuction: 'auction of {brand} {model} {version}',
        },
        negotiationAcceptedBySeller: {
            title: 'Offer accepted!',
            description: 'The seller has accepted your offer of {acceptedAmount} for the {subject}. Negotiation successfully concluded.',
            inAuction: 'negotiation',
            linkedAuction: 'negotiation for {brand} {model} {version}',
        },
        negotiationsDeclinedBySeller: {
            title: 'Negotiation ended!',
            description: 'The seller has definitively rejected your offer of {declinedAmount} for the {subject}. Negotiation ended without success.',
            inAuction: 'negotiation',
            linkedAuction: 'negotiation for {brand} {model} {version}',
        },
        negotiationStoppedProposalsByBuyer: {
            title: 'Last offer!',
            description: 'The buyer has declared that his final offer is {maxBuyerProposal} for the {subject}. It is now up to you to make the final decision.',
            inAuction: 'negotiation',
            linkedAuction: 'negotiation for {brand} {model} {version}',
        },
        userDirectSaleAlreadyPurchased: {
            title: 'Purchase not completed!',
            description: 'Unfortunately, another user won the purchase of {subject} just before you, we are sorry.',
            inDirectSale: 'in this Buy Now',
        },
    },
    privacy: {
        link: 'Privacy Policy',
    },
    seo: {
        slp: {
            [SellingMethod.Auction]: {
                title: 'Used {0} at Auction',
                description: 'Shop online for used {0} at Fleequid.com : easily participate in B2B auctions of the best used {1} at auction.',
            },
            [SellingMethod.DirectSale]: {
                title: 'Used {0} in Buy Now',
                description: 'Shop online for used {0} at Fleequid.com : easily participate in B2B sales of the best used {1} in Buy Now.',
            },
        },
        sdp: {
            title: '{asset} {brand} {model} {version} {vin}',
            ogTitle: '{asset} {brand} {model} {version}',
            [SellingMethod.Auction]: {
                description: 'Buy {brand} {model} {version}, a used {asset} on Fleequid.com: join the B2B auction and place a bid for the {asset} {vin} from brand {brand}, model {model} and version {version}.',
                ogDescription: '{year}, {euroNorm}, {kilometers}: Participate in the B2B auction and place a bid for this used {asset} directly online on Fleequid.com.',
            },
            [SellingMethod.DirectSale]: {
                description: 'Buy {brand} {model} {version}, a used {asset} on http://Fleequid.com: join the Buy Now B2B and make an offer for {asset} {vin} from brand {brand}, model {model}, version {version}.',
                ogDescription: '{year}, {euroNorm}, {kilometers}: Join the Buy Now B2B and make an offer for this used {asset} directly online on http://Fleequid.com.',
            },
        },
        cookiePolicy: {
            title: 'Cookie Policy',
            description: 'Learn about cookie management on the Fleequid portal: discover how we utilize cookies to enhance your browsing experience',
        },
        homepage: {
            title: 'Buses for sale',
            description: 'Buy or sell buses and commercial vehicles through auctions: Join Fleequid and effortlessly participate in top-notch B2B auctions for industrial vehicles',
        },
        privacyPolicy: {
            title: 'Privacy Policy',
            description: 'Privacy policy regarding the Fleequid portal: we clearly explain how we handle browsing data and all your information.',
        },
        signup: {
            title: 'Online vehicle auctions: Registration',
            description: 'Register now on Fleequid to participate easily and conveniently in auctions for new and used industrial vehicles, buses and coaches.',
            confirm: {
                title: 'Email verification',
            },
        },
        termsAndConditions: {
            title: 'Terms and Conditions',
            description: 'Terms and conditions of sale and purchase on Fleequid, the online portal for auctioning and purchasing industrial vehicles and buses.',
        },
        pricingSeller: {
            title: 'Pricing Seller',
            description: 'Auction fees on Fleequid.com: pricing details and specifics on the application method of fees for auctions.',
        },
        pricingBuyer: {
            title: 'Pricing Buyer',
            description: 'Auction fees on Fleequid.com: pricing details and specifics on the application method of fees for auctions.',
        },
        logisticsTermsAndConditions: {
            title: 'Fleequid® Specific Terms and Conditions of Transport Service',
            description: 'Specific Conditions that govern and regulate the terms of the Vehicle Transport Service offered by Fleequid',
        },
        powerTrainTermsAndConditions: {
            title: 'Specific Conditions for the Fleequid® Powertrain Warranty Service',
            description: 'Specific conditions that govern and regulate the terms of the Powertrain warranty service offered by Fleequid',
        },
        accessibilityStatement: {
            title: 'Accessibility Statement Fleequid',
            description: 'Fleequid (Adorea SRL) is committed to making its website accessible, in accordance with Law January 9, 2004, n. 4.',
        },
        sell: {
            title: 'Sell your bus safely | Fleequid',
            description: 'Sell your bus with Fleequid: full process management and secure payments. Reach qualified buyers across Europe.',
        },
        howSellUsedBus: {
            title: 'Sell a used bus: turn necessity into opportunity',
            description: 'All the key aspects to consider—and risks to avoid—when buying a used bus for your fleet.',
        },
        reasonsBuyBusAuction: {
            title: 'Used bus auctions: smart choice for businesses',
            description: 'Transparent pricing, inspected vehicles and secure payments: why buying at Fleequid auctions benefits B2B fleet operators across Europe.',
        },
        guideBuyUsedBus: {
            title: 'Used buses: complete guide to safe purchasing',
            description: 'From vehicle inspection to payments, documentation and transport: the practical guide to buying used buses quickly and transparently across Europe.',
        },
        trustReport: {
            title: 'TrustReport by Fleequid: transparent value for B2B',
            description: 'Objective data, mech/doc checks and photos & video: a neutral standard that streamlines EU sales and remote purchases of industrial vehicles.',
        },
        blackWeek: {
            title: 'Fleequid Black Week',
            description: '50% discount on buyer fees. Do not miss the opportunity.',
        },
    },
    signup: {
        title: 'Create your account',
        subtitle: 'Enter all the necessary data to start using Fleequid',
        companyInfo: 'Company information',
        contactPerson: 'Corporate contact person',
        fields: {
            address: 'Address',
            postCode: 'Postal code',
            city: 'City',
            province: 'Province/region',
            businessName: 'Business name',
            country: 'Country',
            pec: 'Certified email',
            sdi: 'SDI ID',
            tin: {
                base: 'VAT ID',
                placeholder: 'e.g. IT123456789012',
            },
            firstname: 'Firstname',
            lastname: 'Lastname',
            phone: 'Telephone',
            type: {
                label: 'Operator type',
                [OperatorType.Dealer]: 'Dealer',
                [OperatorType.PeopleTransportOperator]: 'Passenger transport operator',
                [OperatorType.Scrapyard]: 'Scrapyard worker',
                [OperatorType.Other]: 'Other',
            },
            email: {
                base: 'Email',
                change: 'Change',
            },
            password: 'Password',
            stats: {
                sales: 'How many buses do you expect to sell in {year}?',
                purchase: 'How many used buses do you expect to purchase in {year}?',
                unknown: "I don't know",
            },
            consent: 'Consent',
            termsConditions: 'I have read and accept the {0}.',
            vexatiousClauses: 'I have read and accept the following clauses:',
            privacy: 'I have read and accept the {0}.',
        },
        hint: {
            phone: {
                text: 'Number format',
                rule: 'Enter the phone in the format +39 1234567890',
            },
        },
        validations: {
            requiredSDIOrPec: 'Enter at least one between SDI code or PEC address',
            companyRegistration: 'The selected file is too large. The maximum file size allowed is {size} MB',
        },
        confirm: {
            success: {
                title: 'Your email is now verified!',
                p1: 'Thank you for confirming your email {email}.',
                p2: 'Your account is now active, and you have full access to all our features.',
                p3: 'If you have any questions or need assistance, contact our support team at {0}. Welcome aboard!',
            },
            error: {
                title: 'Email verification failed',
                p1: "We're sorry, but your email verification was unsuccessful. ",
                p2: 'There may have been an issue with the confirmation link. ',
                p3: 'Please try again by clicking the verification link we sent you. If the problem continues, contact our support team for assistance at {0}.',
            },
            buyCta: 'Get started',
            hpCta: 'Back to homepage',
        },
        createAccount: 'Create account',
        success: {
            title: 'Check your email!',
            subtitle: 'We’ve sent you a link to verify your account.',
            cta: 'Back to login',
        },
        onboarding: {
            company: {
                title: 'Confirm your company details',
                subtitle: 'Review all company details that will be used for invoicing',
            },
            info: {
                title: 'Additional information',
                subtitle: 'Add information for your company',
            },
            contact: {
                title: 'Contact person details',
                subtitle: 'Enter your details as the company contact person',
            },
            thankYou: {
                title: 'Welcome aboard!',
                subtitle: 'Thank you for registering on Fleequid. Your account has been created successfully. We are happy to have you on board. You can now access all the services on the platform.',
                cta: 'Go to Fleequid',
            },
            confirm: 'Confirm and continue',
            skip: 'Skip',
        },
    },
    termsConditions: {
        link: "Terms {'&'} Conditions",
    },
    login: {
        title: 'Enter Fleequid',
        subtitle: 'Access the marketplace dedicated to transport professionals',
        nextButton: 'Continue',
        accountConfirmTitle: 'You have successfully verified your account!',
        accountConfirmDescription: 'You can now log in with your credentials.',
        loginButton: 'Log in to Fleequid',
        email: 'Email',
        password: 'Password',
        forgotPassword: 'Forgot password?',
        signup: "Don't have a Fleequid account? {0}",
        gatedActions: {
            title: 'To {actionName}, you must first log in or register.',
            description: 'Once signed in, you will return to the page you were viewing.',
            makeAnOffer: 'make an offer',
            buy: 'purchase a vehicle',
            approve: 'approve the listing',
            addToFavorites: 'add to favorites',
        },
    },
    passwordReset: {
        request: {
            title: 'Reset your password',
            subTitle: 'Enter your email and we will send you a message to reset your password.',
            submitButton: 'Send email',
            onSuccessTitle: 'Request accepted',
            onSuccessDescription: 'You will receive an email with the procedure to complete the password reset.',
        },
        reset: {
            title: 'Reset your password',
            description: 'Enter a new password. Once the change is confirmed, all active sessions will be invalidated.',
            newPassword: 'New password',
            confirmNewPassword: 'Confirm new password',
            submitButton: 'Change password',
            updateSuccess: 'Password changed successfully',
            tokenExpired: 'The password reset token has expired.',
            tokenInvalid: 'The password reset token is invalid.',
            startOverDescription: 'Start over the password reset process, so a new token will be generated.',
            startOverButton: 'Try again',
        },
    },
    validation: {
        msg: {
            characters: 'One or more invalid characters',
            email: "Email should resemble user{'@'}domain.ext",
            incomplete: '{field} is incomplete',
            invalid: '{field} invalid',
            invalidBid: 'The amount inserted is not valid',
            missing: '{field} is missing',
            max: 'Max length {count} character | Max length {count} characters',
            min: 'Min length {count} character | Min length {count} characters',
            length: 'The length must be exactly of {count} character | The length must be exactly of {count} characters',
            passwordWeak: 'Password too weak',
            passwordMatch: 'Passwords do not match',
            phone: 'Please use only digits, blanks and the + symbol',
            recaptcha: 'reCAPTCHA validation error',
            required: '{field} is Required',
        },
        hint: {
            password: {
                text: 'Rules for a strong password',
                rules: {
                    length: 'At least 8 characters',
                    uppercase: '1 lowercase',
                    lowercase: '1 uppercase',
                    symbol: '1 symbol',
                    number: '1 number',
                },
            },
        },
    },
    profile: {
        title: 'Your profile',
        companyData: {
            readOnlyDisclaimer: 'The company data is read-only, to request changes contact us at {0}.',
        },
        userData: {
            saveChanges: 'Confirm new data',
            cancelChanges: 'Cancel changes',
            updateSuccess: 'Personal data saved correctly',
        },
        changePassword: {
            title: 'Change password',
            description: 'Changing your password will invalidate all other active sessions except the current one.',
            currentPassword: 'Current password',
            newPassword: 'New password',
            confirmNewPassword: 'Confirm new password',
            submitButton: 'Change password',
            updateSuccess: 'Password changed successfully',
        },
        changeEmail: {
            title: 'Change email',
            description: 'You will receive an email with a link to confirm the new email. Changing your email will invalidate all your active sessions.',
            password: 'Current password',
            email: 'Email',
            submitButton: 'Change email',
            updateSuccess: 'Email changed successfully',
        },
    },
    sessionExpired: {
        title: 'Your session is expired',
        description: 'The session has been invalidated because the credentials were changed. Please login with the new credentials.',
        close: 'Continue',
    },
    nuxtSiteConfig: {
        description: 'Buy or sell buses and commercial vehicles through auctions: Join Fleequid and effortlessly participate in top-notch B2B auctions for industrial vehicles',
    },
    auctionNegotiation: {
        yourRequest: 'Your Ask',
        sellerRequest: 'Seller’s Ask',
        yourOffer: 'Your Offer',
        buyerOffer: 'Buyer’s Offer',
        updateRequest: 'Change your ask',
        updateOffer: 'Change your offer',
        acceptRequest: 'Accept the ask',
        acceptOffer: 'Accept the offer',
        buyerStopProposals: 'Lock the current offer',
        sellerDecline: 'End negotiation',
        cancel: 'Cancel',
        proposalModal: {
            title: 'Negotiate the price',
            subtitle: 'You have {countdown} to negotiate the offer.',
            resetProposal: 'reset',
            stopProposalsLabel: 'Maximum amount I’m willing to offer',
            stopProposalsDescription: 'If you select this option, the seller can no longer negotiate, but only accept or reject this offer.',
        },
        acceptModal: {
            titleForSeller: 'Do you accept the offer?',
            titleForBuyer: 'Do you accept the ask?',
        },
        sellerDeclineModal: {
            title: 'End the negotiation',
            disclaimer: 'By doing so, you end the auction and lose all received offers. Our team will contact you soon to reschedule the auction.',
            confirmButton: 'End the negotiation',
        },
        buyerStopProposalsModal: {
            title: 'Lock the current offer',
            disclaimer: 'By doing so, the seller can no longer negotiate, but only accept or reject this offer.',
            confirmButton: 'Lock the current offer',
        },
        notifications: {
            proposalByBuyer: {
                title: 'Offer changed!',
                description: 'You changed your offer to {amount} for the negotiation.',
            },
            proposalBySeller: {
                title: 'Ask changed!',
                description: 'You changed your ask to {amount} for the negotiation.',
            },
            acceptByBuyer: {
                title: 'Ask accepted!',
                description: 'You accepted the seller’s ask of {amount}. Negotiation successfully closed.',
            },
            acceptBySeller: {
                title: 'Offer accepted!',
                description: 'You accepted the buyer’s offer of {amount}. Negotiation successfully closed.',
            },
            declinedBySeller: {
                title: 'Negotiation ended!',
                description: 'You definitively declined the buyer’s offer of {amount}. Negotiation closed without success.',
            },
            stopProposalsByBuyer: {
                title: 'Final offer confirmed!',
                description: 'Your final offer for the negotiation is {amount}. Wait for the seller’s final decision.',
            },
            apiError: {
                title: 'An unexpected error occurred.',
                description: 'The requested operation could not be completed. Please refresh the page and try again.',
            },
        },
    },
    directSaleNegotiation: {
        directSalePrice: 'Sale price',
        startNegotiationButton: 'Offer less',
        sellerNegotiationsMenu: {
            itemTitle: 'Negotiation',
            accepted: 'Negotiation closed at',
            successful: 'Successful negotiation',
            lastOffer: 'Last offer',
            expired: 'This negotiation has expired',
        },
        proposalModal: {
            firstOfferTitle: 'Make your offer for the vehicle',
            firstOfferSubtitle: 'Making a new offer for this vehicle will open a negotiation with the seller',
            bailNotice: 'A valid payment method for the security deposit will be requested to join the negotiation.',
            buyerTitle: 'Edit your offer',
            buyerSubtitle: 'Make a new price offer for the vehicle. The last confirmed offer will be binding',
            sellerTitle: 'Edit your request',
            sellerSubtitle: 'Make a new price request for the vehicle. The last confirmed request will be binding',
            resetProposal: 'reset',
        },
        acceptModal: {
            sellerTitle: 'Accept the offer',
            buyerTitle: 'Accept the request',
            buyerConfirmInfo: "You are about to accept the seller's latest request. Once confirmed, you commit to complete the purchase successfully.",
            sellerConfirmInfo: "You are about to accept the buyer's latest offer. Once confirmed, you commit to complete the sale successfully.",
            buyerBailReleaseNotice: 'Once the purchase is paid for, we will release the security-deposit amount to the selected payment method.',
            buyerBailCaptureDisclaimer: 'Remember: otherwise, Fleequid reserves the right to retain the security deposit',
        },
        firstOfferInProgressModal: {
            title: 'We are processing your offer',
        },
        notifications: {
            firstProposalByBuyer: {
                title: 'Request sent!',
                description: 'You have sent the seller your offer of {amount} for the negotiation.',
            },
            proposalByBuyer: {
                title: 'Offer updated!',
                description: 'You updated your offer to {amount} for the negotiation.',
            },
            proposalBySeller: {
                title: 'Request updated!',
                description: 'You updated your request to {amount} for the negotiation.',
            },
            acceptByBuyer: {
                title: 'Request accepted!',
                description: "You accepted the seller's request of {amount}. Negotiation successfully completed.",
            },
            acceptBySeller: {
                title: 'Offer accepted!',
                description: "You accepted the buyer's offer of {amount}. Negotiation successfully completed.",
            },
            apiError: {
                title: 'An unexpected error occurred.',
                description: 'The requested operation could not be completed. Refresh the page and try again.',
            },
            negotiationExpired: {
                title: 'Negotiation expired!',
                description: 'The time for the {subject} has elapsed. Negotiation ended unsuccessfully.',
                inDirectSale: 'negotiation',
                linkedDirectSale: 'negotiation of {brand} {model} {version}',
            },
            negotiationProposalByBuyer: {
                title: 'Offer updated!',
                description: 'A buyer updated their offer to {proposalAmount} for the {subject}.',
                inDirectSale: 'negotiation',
                linkedDirectSale: 'negotiation of {brand} {model} {version}',
            },
            negotiationProposalBySeller: {
                title: 'Request updated!',
                description: 'The seller updated their request to {proposalAmount} for the {subject}.',
                inDirectSale: 'negotiation',
                linkedDirectSale: 'negotiation of {brand} {model} {version}',
            },
            negotiationAcceptedByBuyer: {
                title: 'Request accepted!',
                description: 'A buyer accepted your request of {acceptedAmount} for the {subject}. Negotiation successfully completed.',
                inDirectSale: 'negotiation',
                linkedDirectSale: 'negotiation of {brand} {model} {version}',
            },
            negotiationAcceptedByBackOffice: {
                title: 'Request accepted!',
                description: 'The {subject} has been successfully concluded at the amount of {acceptedAmount}, congratulations.',
                inDirectSale: 'negotiation',
                linkedDirectSale: 'negotiation of {brand} {model} {version}',
            },
            negotiationAcceptedBySeller: {
                title: 'Offer accepted!',
                description: 'The seller accepted your offer of {acceptedAmount} for the {subject}. Negotiation successfully completed.',
                inDirectSale: 'negotiation',
                linkedDirectSale: 'negotiation of {brand} {model} {version}',
            },
            negotiationAbortedAnotherAcceptance: {
                title: 'Purchase not completed!',
                description: 'Unfortunately another user purchased {subject} just before you. Your security deposit has been released.',
                inDirectSale: 'in this buy now',
                linkedDirectSale: 'in the buy now of {brand} {model} {version}',
            },
        },
    },
    negotiation: {
        headerTitle: 'Vehicle negotiation',
        yourRequest: 'Your request',
        sellerRequest: "Seller's request",
        yourOffer: 'Your offer',
        buyerOffer: "Buyer's offer",
        updateRequest: 'Edit your request',
        updateOffer: 'Edit your offer',
        makeOffer: 'Make your offer',
        acceptRequest: 'Accept the request',
        acceptOffer: 'Accept the offer',
        negotiations: 'Negotiations',
        cancel: 'Cancel',
        expiredAt: 'Ended on {date} at {time}',
        summary: 'Summary',
        goToNegotiation: 'Go to negotiation',
        showNegotiations: 'Show negotiations',
        stateBadge: {
            inProgress: 'Ongoing',
            accepted: 'Completed',
            expired: 'Expired',
            underSellerReview: 'Final offer',
        },
        state: {
            [NegotiationState.Open]: 'In progress',
        },
        proposalsHistory: {
            title: 'History',
            itemTitle: 'Negotiation ended on {date}',
            sellerAsSeller: 'You (Seller)',
            buyerAsSeller: 'Buyer',
            buyerAsBuyer: 'You (Buyer)',
            sellerAsBuyer: 'Seller',
        },
        sellerAcceptedAlert: {
            acceptedByBuyerTitle: 'The buyer has accepted your request',
            acceptedBySellerTitle: 'You accepted the buyer’s offer',
            description: 'Now you have to wait for the buyer to complete the purchase properly',
        },
        buyerAcceptedAlert: {
            acceptedByBuyerTitle: 'You accepted the seller’s request',
            acceptedBySellerTitle: 'The seller accepted your offer',
            description: 'The vehicle is now yours, configure your order to include additional services',
        },
    },
    salesContact: {
        card: {
            title: 'Contact our expert',
            whatsAppLinkSlpTitle: 'I am interested in a vehicle with these characteristics. Is anything available?',
            whatsAppLinkMessage: "I'm interested and would like to be contacted, thank you.",
        },
        contactForm: {
            title: 'Contact your trusted representative',
            name: 'Name',
            email: 'Email address',
            phone: 'Phone number',
            company: 'Company',
            message: 'Message',
            messageSalePrefill: 'I am interested in {title}',
            messageAppliedFilterPrefill: 'I am interested in a vehicle with these characteristics. Is there anything available?',
            consentNotice: 'By submitting this request, you confirm you agree to the {terms} and have read the {privacy}.',
            submitButton: 'Send request',
            submitSuccess: 'Request sent',
        },
    },
    customerServiceContact: {
        title: 'CONTACT SUPPORT',
        description: 'Customer support service',
    },
    sell: {
        hero: {
            title: 'Do you have a bus to sell? Start here.',
            description: 'Reach buyers across Europe and sell at the best price, worry-free. You will be contacted within 72 hours by one of our representatives to begin the evaluation of your vehicle.',
            items: {
                item1: 'Only verified professional buyers',
                item2: 'Secure and guaranteed payments',
                item3: 'Support and assistance throughout the entire negotiation',
            },
        },
        sellForm: {
            busStep: 'What do you want to sell?',
            contactsStep: 'Your contacts',
            vehiclesNumber: {
                base: 'How many vehicles do you want to sell?',
                field: 'Quantity',
            },
            vehiclesClasses: {
                base: 'Choose the class',
                field: 'Class',
            },
            vehiclesEuroNorms: {
                base: 'Choose emission standard',
                field: 'Emission standard',
            },
            vehiclesAddress: { base: 'Where are they located?', field: 'Address' },
            notes: 'Add useful details here',
            busCta: {
                user: 'Submit request',
                guest: 'Next step',
            },
            country: 'Country',
            company: {
                base: 'Company VAT ID',
                field: 'VAT ID',
                placeholder: 'Start typing',
            },
            email: {
                base: 'Email address',
                field: 'Email',
            },
            firstname: 'Contact person first name',
            lastname: 'Contact person last name',
            phone: 'Phone number',
            contactsCta: 'Submit request',
        },
        success: {
            title: 'Request submitted!',
            description: 'We have received all the data from your request. One of our consultants will contact you within 72 hours.',
            guest: {
                title: 'Not yet registered on Fleequid?',
                description: 'You will be able to participate in auctions and view all vehicles on our website',
                cta: 'Register now',
            },
        },
        sectionSelling: {
            title: 'How does selling work?',
            items: {
                item1: {
                    label: 'Fill out the form and register',
                    content: 'Enter your contact details, the data of the vehicles you want to sell, and create an account on Fleequid.',
                },
                item2: {
                    label: 'Wait for our call',
                    content: 'Within 72 hours you will be contacted by one of our experts to schedule an appointment for our inspection.',
                },
                item3: {
                    label: 'We create the TrustReport',
                    content: 'On the agreed day, our expert will arrive at the vehicle location to perform a thorough inspection of the vehicle, our TrustReport.',
                },
                item4: {
                    label: 'Receive our feedback',
                    content: 'Depending on the characteristics and condition of the vehicle, our expert will suggest the best price and ideal selling method, between Auction and Buy Now.',
                },
                item5: {
                    label: 'Approve the listing and start receiving offers',
                    content: 'Once price and selling method are defined, our team will create the listing and send it to you for approval. As soon as it is approved, the listing will be immediately available on Fleequid.',
                },
            },
        },
        fleequidBenefits: {
            title: 'The advantages of Fleequid for those who want to sell a bus',
            description: {
                base: '{bold} connects professional sellers with qualified international buyers through a reliable, transparent and efficient process. We transform experience, data and expertise into better and safer sales.',
                bold: 'Fleequid',
            },
            p1: 'Thanks to Fleequid you have:',
            items: {
                item1: 'Vehicle inspection and listing creation carried out by our team, directly at your location.',
                item2: 'International network of professional buyers.',
                item3: 'Verified offers and guaranteed payments.',
                item4: 'Support throughout the entire process.',
                item5: 'No costs until the sale.',
            },
            p2: 'We take care of the entire process, so you can focus on your business.',
        },
        faq: {
            title: 'Frequently asked questions',
            items: {
                item1: {
                    label: 'How can I start selling on Fleequid?',
                    content: {
                        base: '{link} and you will be contacted by one of our experts, who will guide you through the entire process.',
                        link: 'Fill out the form',
                    },
                },
                item2: {
                    label: 'What documents do I need to provide to sell a vehicle?',
                    content: 'You will be asked for a copy of the registration certificate and the Certificate of Conformity, if available, for the vehicle to be put up for sale, for the necessary checks by our team. One of our experts will subsequently inspect the vehicle in person to create the TrustReport, an impartial verification of over 150 checkpoints and over 80 photos and videos of the vehicle.',
                },
                item3: {
                    label: 'What is the TrustReport?',
                    content: 'The TrustReport is a thorough inspection of the vehicle, carried out by our specialized technicians through a process developed over the years. The TrustReport will be an integral part of the online listing on Fleequid.',
                },
                item4: {
                    label: 'Who creates the TrustReport?',
                    content: 'The TrustReport is created by one of our qualified inspectors, directly at the location where the vehicle to be sold is located.',
                },
            },
        },
        sectionEasySelling: {
            title1: 'Selling a bus is complicated.',
            title2: 'Fleequid makes it easy.',
            cta: 'Submit your request',
        },
    },
    howSellUsedBus: {
        hero: {
            title: 'Selling a used bus: how to turn a need into an opportunity',
            description1: {
                base: '{bold} can quickly become a complex process: finding reliable buyers, defining the right value, managing negotiations, and ensuring secure payment are just some of the main challenges.',
                bold: 'Selling a used bus in the B2B market',
            },
            description2: {
                base: '{bold1}, simplifying every step of the sale and {bold2}.',
                bold1: 'Fleequid was created to address exactly these needs',
                bold2: 'maximizing the value of the vehicle',
            },
            cta: 'How to sell your used bus',
        },
        sectionReach: {
            title: 'Reach truly interested buyers',
            description1: 'One of the biggest obstacles in selling used buses is the difficulty in reaching professional and qualified buyers. Traditional channels offer limited visibility and often attract non-targeted contacts.',
            description2: 'Fleequid solves this problem thanks to a European network of verified B2B buyers, active and looking for specific vehicles. Each vehicle is presented within an auction accessible from all over Europe, with thousands of potential buyers ready to bid.',
        },
        sectionEnhance: {
            title: 'Enhance the vehicle with a competitive price',
            description1: 'Determining a fair price and defending the value of your vehicle is one of the main challenges. Private negotiations can lead to forced price reductions or weeks of waiting without results.',
            description2: {
                base: '{bold}: the seller sets a threshold below which the vehicle will not be awarded, thus ensuring not to lose value.',
                bold: 'Fleequid adopts a reserve price auction system',
            },
            description3: 'In addition, offers from potential buyers are supported by a mandatory security deposit, ensuring seriousness and commitment to the bid.',
            cta: 'Sell your vehicle',
        },
        sectionNegotiationTimesAndRisks: {
            title: 'Reduce negotiation times and risks',
            description1: 'Direct negotiations are often long, disorganized, and difficult to manage, especially when involving international parties.',
            description2: 'With Fleequid, the entire sales process is clear and digitalized. All registered buyers are verified professionals, and their offers are binding thanks to the security deposit.',
            description3: 'And if the reserve price is not reached, you have the possibility to negotiate directly from the auction page with the highest bidder, without wasting time.',
        },
        sectionPaymentSecurelyAndReliably: {
            title: 'Receive payment securely and reliably',
            description1: 'In the B2B market, one of the main risks is payment, especially in international transactions.',
            description2: 'Fleequid directly manages the entire payment process.',
            description3: {
                base: 'Once the auction is awarded, {bold1} from the issuance of the invoice, {bold2}.',
                bold1: 'the seller receives the amount for their vehicle within 1 business day',
                bold2: 'eliminating the risk of missed payments or delays',
            },
        },
        sectionManageDocumentationAndBureaucracyWithoutWorries: {
            title: 'Manage documentation and bureaucracy without worries',
            description1: 'The ownership transfer of a used bus can be complicated, with regulations varying from country to country.',
            description2: 'Properly preparing the documentation, verifying technical and administrative requirements, and supporting export is a demanding job.',
            description3: {
                base: '{bold}, simplifying the process for the seller.',
                bold: 'Fleequid offers complete support for document management',
            },
        },
        sellNowCta: 'Sell your used bus now',
        whySell: {
            title: 'Why sell on Fleequid?',
            description: 'Fleequid is designed to sell used buses in a professional, secure, and results-oriented way. From publication to payment, every step is designed to enhance your vehicle, offering you two complementary paths: auction and immediate sale with Buy Now.',
            items: {
                item1: 'Access a network of verified and active potential buyers from all over Europe',
                item2: 'Auctions with reserve price',
                item3: 'Secure payment',
                item4: 'Support in the preparation of documentation',
                item5: 'Professional vehicle inspection by our team',
                item6: 'All offers from potential buyers are guaranteed by a security deposit',
            },
        },
        sectionPlatformDesignedToEnhanceYourFleet: {
            title: 'Fleequid: the platform designed to enhance your fleet',
            description: 'Fleequid is the first European marketplace entirely dedicated to the auction sale of used buses between companies. A digital channel designed to sell professionally, quickly, and securely, increasing the visibility of your fleet and reaching new markets.',
            cta: 'Sell now',
        },
        sectionStandardThatImprovesTheEntireProcess: {
            title: 'A standard that improves the entire process',
            description1: {
                base: '{bold1}. Instead of relying on vague descriptions or one-sided statements, sellers and buyers have at their disposal a third-party, neutral and professional tool that {bold2}.',
                bold1: 'The introduction of {primary} represents a qualitative step forward for the B2B used vehicle market',
                primary: 'TrustReport',
                bold2: 'improves the efficiency and transparency of the entire transaction',
            },
            description2: {
                base: 'Whether you are trying to sell a vehicle from your fleet or to buy the next bus for your services, {primary} {bold}.',
                bold: 'is the guarantee that all the information that really matters is already just a click away',
                primary: 'TrustReport',
            },
            cta: 'Discover all vehicles',
        },
        faq: {
            title: 'Frequently Asked Questions',
            items: {
                item1: {
                    title: 'Do I have to pay anything before my bus is put up for auction?',
                    content: 'No. The inspection and creation of the TrustReport are covered by Fleequid. Sales commissions are withheld only after the award and are transparently shown in the assignment contract.',
                },
                item2: {
                    title: 'How is the reserve price determined?',
                    content: 'You define it together with our Sales Representative, who provides you with market data and benchmarks from recent auctions. The reserve price remains hidden from potential buyers and ensures that the vehicle is not sold below the established value.',
                },
                item3: {
                    title: 'When do I receive the payment after the sale?',
                    content: 'Within 1 business day from the receipt of your invoice: Fleequid collects the full amount from the buyer, verifies the payment, and pays you directly, eliminating the risk of defaults or international delays.',
                },
            },
        },
    },
    reasonsBuyBusAuction: {
        hero: {
            title: 'The reasons to buy a bus at auction: a smart choice for companies and operators',
            description1: 'In the world of professional transport, buying a used bus can be a long and uncertain process, especially when looking for guarantees on quality, price, and delivery times.',
            description2: {
                base: '{bold} for those who want to buy vehicles quickly, safely, and at real market conditions.',
                bold: 'online auctions are today one of the most efficient and transparent solutions',
            },
            description3: 'Fleequid, a European platform specialized in the auction sale of used buses, is designed to support every stage of this process.',
            cta: 'Reasons to buy at auction',
        },
        sectionWhyBuy: {
            item1: {
                title: 'Competitive and transparent price',
                description: {
                    base: 'One of the main advantages of buying at auction is the possibility of securing a vehicle at a {bold}, without unnecessary surcharges or intermediaries.',
                    bold: 'price aligned with the real market',
                },
                fleequid: 'Fleequid auctions are public, transparent, and accessible: every bid is visible and tracked, allowing the real value of the vehicle to be established in a dynamic and competitive way.',
            },
            item2: {
                title: 'Wide selection of verified vehicles',
                description: 'Buying at auction does not mean giving up quality.\n' + 'Each vehicle is accompanied by a complete technical dossier, prepared by a specialized team that evaluates the vehicle directly at the seller’s location. Detailed photos and technical information allow for an accurate assessment even remotely.',
                fleequid: 'On Fleequid you will find a constantly updated fleet, consisting of buses, coaches, and minibuses professionally inspected.',
            },
            item3: {
                title: 'Quick and simplified decisions',
                description: 'In the B2B market, time is a crucial factor. Fleequid auctions have a defined duration, and the purchase process is completed in just a few days.',
                fleequid: 'Once the vehicle is awarded, you will receive operational support until the completion of the transaction. This approach speeds up decision-making and allows you to better plan the integration of the vehicle into your fleet.',
            },
            item4: {
                title: 'Secure payment and transaction',
                description: 'Once the auction is closed, payment is managed through a guaranteed process that protects both the buyer and the seller. No worries about international transfers or unexpected delays.',
                fleequid: 'Every purchase is supported by a secure and traceable payment system.',
            },
        },
        buyCta: 'Buy your bus at auction now',
        sectionTrust: {
            item1: {
                title: 'Risk-free remote purchase',
                description: {
                    base: 'Thanks to the completeness of {primary}, professional images, and the objective evaluation of the vehicle’s condition, it is possible to {bold}, without the need for on-site inspections. This allows you to expand your search across the entire European market, optimizing time and resources.',
                    primary: 'TrustReport',
                    bold: 'buy vehicles even remotely',
                },
            },
            item2: {
                title: 'Access to vehicles from all over Europe',
                description: {
                    base: 'With Fleequid you can buy vehicles from {bold}, accessing a wider market compared to local or generalist channels. Each listing is designed for industry operators and offers a level of technical and commercial detail suitable for those making targeted investments.',
                    bold: 'professional sellers across Europe',
                },
            },
        },
        sectionFindNextBus: {
            title: 'Start looking for your next bus now',
            cta: 'Discover all vehicles',
        },
        faq: {
            title: 'Frequently Asked Questions',
            items: {
                item1: {
                    title: 'How long does an auction on Fleequid usually last?',
                    content: 'Each auction has a fixed duration of 7 days: you immediately know when it starts and when it ends, so you can organize your budget and time without surprises.',
                },
                item2: {
                    title: 'How do I know if the vehicle is really in good condition without seeing it in person?',
                    content: 'All vehicles listed on Fleequid are inspected on-site by our team of professionals. Each vehicle is accompanied by a complete dossier with photos, videos, and an objective report, so you can evaluate remotely without having to arrange inspections.',
                },
                item3: {
                    title: 'Who handles export documents or re-registration if the vehicle comes from another EU country?',
                    content: 'Our Customer Service team assists you in collecting the necessary documents and checks for any requirements (inspection, Euro standard). We also offer additional services such as logistics and registration.',
                },
            },
        },
    },
    guideBuyUsedBus: {
        hero: {
            title: 'Guide to buying a used bus at auction',
            description1: 'Buying a used bus may seem simple, but in reality it involves many complexities, especially for companies and operators who want to make safe and functional choices.',
            description2: {
                base: '{bold1} among the main challenges to consider {bold2}.',
                bold1: 'This guide takes you step by step',
                bold2: 'and explains how Fleequid can support you in dealing with them efficiently, safely, and professionally',
            },
            cta: 'Find out how to buy easily and safely',
        },
        sectionEnsureVehicleCondition: {
            title: 'Ensure the real condition of the vehicle',
            description1: 'Accurately assessing the condition of a used bus is the first step toward a conscious purchase.',
            description2: 'In a European context, vehicles for sale may be located hundreds or even thousands of kilometers away.',
            description3: 'Organizing physical inspections is not always feasible, especially within tight deadlines.',
            description4: 'When you cannot view the vehicle in person, the risk is encountering undeclared issues.',
            description5: 'Fleequid addresses this need with a standardized procedure: every bus is inspected by our specialized team directly at the seller’s location.',
            description6: {
                base: '{bold}, which includes photographs, mechanical analyses, and an objective description of the vehicle’s condition.',
                bold: 'The result is the TrustReport',
            },
        },
        sectionSteps: {
            item1: {
                title: 'Manage a secure payment',
                description: 'Making a payment without guarantees is one of the main obstacles for the buyer. The fear of fraud or mishaps can block even the best opportunities.',
                fleequid: 'Solves this problem by acting as a reliable intermediary between the parties. Even security deposits are handled through safe and guaranteed payment methods.',
            },
            item2: {
                title: 'Prepare the necessary documentation',
                description1: 'Each European country has specific rules for ownership transfer and registration of used buses.',
                description2: 'For buyers, collecting and validating the required documents can become a complex process.',
                fleequid: 'Supports the buyer in the process of collecting and verifying documentation, and flags in advance any administrative constraints (such as vehicles with expired inspections, for example).',
            },
            item3: {
                title: 'Find the right vehicle for your needs',
                description1: 'Often the real difficulty is not buying, but finding the right bus, with the technical features and quality level you are looking for.',
                description2: 'Generalist platforms or unverified ads do not guarantee constant updates or transparency.',
                fleequid: 'Offers a wide fleet, selected from professional sellers and updated daily. Advanced filters allow you to search for the ideal vehicle by type, year, mileage, power, fuel type, and other parameters.',
            },
            item4: {
                title: 'After-sales assistance and services',
                description1: 'Once you have purchased the bus, you still need to manage transport, registration, and all operational activities that follow the transaction.',
                description2: 'Without adequate support, these stages risk becoming a bottleneck.',
                fleequid: 'Accompanies the buyer even after the sale, all the way to getting the vehicle on the road, thanks to a wide range of additional services, always with a dedicated person by your side.',
            },
        },
        buyCta: 'Buy your used bus now',
        sectionWhyFleequid: {
            title: 'Why buying with Fleequid is convenient',
            item1: 'Every bus is inspected by a specialized team',
            item2: 'Access guaranteed vehicles across Europe',
            item3: 'The entire process is 100% online, secure, and traceable',
            item4: 'Operational support at every stage of the process',
            item5: 'Post-purchase services',
        },
        sectionFindNextBus: {
            title: 'Start looking for your next bus now',
            cta: 'Discover all vehicles',
        },
        faq: {
            title: 'Frequently Asked Questions',
            items: {
                item1: {
                    title: 'How can I check the real condition of a bus that is hundreds of kilometers away?',
                    content: 'All vehicles listed on Fleequid are inspected on-site by our team of professionals. Each vehicle comes with a complete dossier including photos, videos, and an objective report, so you can evaluate remotely without organizing inspections.',
                },
                item2: {
                    title: 'How does Fleequid guarantee payment security?',
                    content: 'The payment is managed directly by Fleequid: the buyer transfers the amount to us, we verify the credit, and pay the seller within one business day of receiving the invoice.',
                },
                item3: {
                    title: 'Who helps me with international transfer and registration documents?',
                    content: 'Our Customer Service supports you in collecting and verifying all documents required by the destination country, highlighting in advance any constraints (expired inspections, Euro standards, etc.).',
                },
                item4: {
                    title: 'What happens after purchase: do I need to arrange transport and inspection myself?',
                    content: 'No. Thanks to our network of partners, we can organize transport, assist you with registration, inspections, and all post-sale operations until the bus is on the road.',
                },
            },
        },
    },
    trustReport: {
        hero: {
            title: {
                base1: "Fleequid's {highlight}:",
                base2: 'transparency and value for buyers and sellers',
                highlight: 'TrustReport',
            },
            description1: {
                base: 'When it comes to {bold}',
                bold: 'buying or selling a used bus, transparency is everything',
            },
            description2: 'In a fragmented and often opaque market like industrial vehicles, the risk of incomplete or unreliable information is high.',
            description3: {
                base: 'For this reason, every vehicle published on Fleequid is first inspected by one of our expert technicians, to produce the {highlight}: a tool designed to ensure safety, trust, and speed in negotiations, with tangible benefits for both sellers and buyers.',
                highlight: 'TrustReport',
            },
            cta: 'Discover TrustReport',
        },
        sectionWhatIs: {
            title: 'What is the TrustReport?',
            description1: {
                base: 'Fleequid’s {bold} is a detailed vehicle report that accompanies every unit listed on the platform. It is prepared by our team of specialized inspectors, who visit the seller in person to assess the vehicle on site.',
                bold: 'TrustReport',
            },
            description2: {
                base: 'The {bold} is an integral part of the listing and can be freely viewed by all potential buyers, directly on the vehicle page.',
                bold: 'TrustReport',
            },
        },
        sectionHowItWorks: {
            title: {
                base: 'How the {highlight} works:',
                highlight: 'TrustReport',
            },
            item1: 'One of our specialized technicians visits the seller, wherever they are.',
            item2: 'We inspect the vehicle in detail, independently and following our standard procedure, checking over 150 points of interest.',
            item3: 'We take over 80 photos and videos to transparently show the vehicle’s actual condition',
            item4: 'All documents are checked and verified.',
            item5: 'Once the inspection is complete, the listing is published and the TrustReport becomes available to all potential buyers',
        },
        sectionProfessionalTeam: {
            title: 'A team of professionals at your service',
            description: {
                base: '{highlight} is a comprehensive report, prepared by our technical team directly at the seller’s premises:',
                highlight: 'TrustReport',
            },
            item1: 'A specialized team of professionals',
            item2: 'Standardized procedure for the vehicle report',
            item3: 'Directly at the seller’s location',
        },
        sectionHistoryAnalysis: {
            title: 'Document and history analysis of the vehicle',
            description: 'We know how important it is to verify the correctness of the available documentation and the presence of any encumbrances on the vehicle, and our team of professionals does this for you through thorough checks.',
            item1: 'Verification of inspection status',
            item2: 'Verification of registration document correctness',
            item3: 'Verification of the vehicle’s history',
            item4: 'Verification of any liens on the vehicle',
        },
        sectionBodyInteriors: {
            title: 'Condition of exterior body and interiors',
            description: 'We check, photograph, and document the state of the exterior body and interiors, so that no purchase is a surprise.',
            item1: 'Complete photo and video documentation of the exterior body and interiors',
            item2: 'Documentation of any damage and verification of undeclared scratches',
            item3: 'Verification of tire condition',
            item4: 'Verification of the condition of the interior seats',
        },
        sectionGeneralState: {
            title: 'Overall condition of the vehicle',
            description: 'We take you to the heart of your next vehicle, with detailed photographic documentation of the engine and the available equipment:',
            item1: 'Verification of the operation of the available equipment',
            item2: 'Verification of the external condition of the engine',
            item3: 'Verification of the external condition of the gearbox',
            item4: 'Verification of the condition of the braking system',
            item5: 'Assessment of consistency between vehicle condition and mileage',
        },
        discoverAllVehiclesCta: 'Discover all available vehicles',
        sectionBuyerBenefits: {
            title: 'Benefits for buyers',
            description: 'For a professional audience, every purchase must be considered and rational, especially when it is not possible to view the vehicle in person.',
            item1: 'Make informed decisions thanks to objective, verified data',
            item2: 'Evaluate the vehicle remotely thanks to a detailed report',
            item3: 'Avoid unpleasant surprises: presence of damage or items requiring attention',
            item4: 'Save time with clear and complete information',
            item5: 'Compare different vehicles on objective criteria',
        },
        sectionSellerBenefits: {
            title: 'Benefits for sellers',
            description: {
                base: 'For professional sellers too, the {highlight} is an important sales support tool.',
                highlight: 'TrustReport',
            },
            item1: 'Enhances the vehicle through a professional presentation',
            item2: 'Speeds up negotiations by providing all necessary information from the outset',
            item3: 'Reduces the number of questions and requests for clarification from interested users',
            item4: 'Increases trust in the vehicle and in the negotiation, reducing hesitation',
            item5: 'Facilitates international sales, being available in 11 different languages',
        },
        sectionStandardEnhancement: {
            title: 'A standard that improves the entire process',
            description1: {
                base: 'The introduction of {highlight} marks a qualitative turning point for the B2B used market. Instead of relying on sketchy descriptions or unilateral statements, sellers and buyers have at their disposal a third-party, neutral, professional tool that improves the efficiency and transparency of the entire transaction',
                highlight: 'TrustReport',
            },
            description2: {
                base: 'Whether you are trying to sell a unit from your fleet or to buy the next bus for your services, {highlight} by Fleequid guarantees that all the truly important information is already just a click away.',
                highlight: 'TrustReport',
            },
            cta: 'Discover all vehicles',
        },
        faq: {
            title: 'Frequently Asked Questions',
            items: {
                item1: {
                    title: 'What is Fleequid’s TrustReport and why is it important?',
                    content: 'The TrustReport is a comprehensive report on the vehicle’s condition, accompanied by photos and videos, enabling you to make an informed purchase with no surprises.',
                },
                item2: {
                    title: 'Who prepares Fleequid’s TrustReport and which standards does it follow?',
                    content: 'The TrustReport is prepared by our team of professionals, who strictly follow a standardized vehicle survey procedure with checks on all main components, so that all dossiers are complete and comparable.',
                },
                item3: {
                    title: 'Who carries out vehicle inspections abroad?',
                    content: 'Our in-house team of professionals is active across Europe; we have a physical presence in every European country',
                },
                item4: {
                    title: 'Are there vehicles for sale without a TrustReport?',
                    content: 'No. On Fleequid every vehicle is always accompanied by a validated technical TrustReport. Publication is only possible after inspection, to ensure full transparency and safety in the transaction.',
                },
            },
        },
    },
    blackWeek: {
        hero: {
            title: {
                base1: 'Black Week on Fleequid',
                base2: 'Fees halved. Opportunities doubled.',
                highlight: 'Only from 24 to 28 November',
            },
            description: 'Save 50% on buyer fees. Only verified vehicles, with maximum value.',
            cta: 'Discover the offer',
        },
        countdown: {
            title: 'The offer ends in:',
            days: 'Days',
            hours: 'Hours',
            minutes: 'Minutes',
            seconds: 'Seconds',
        },
        sectionWhy: {
            title: 'Why take advantage of Black Week?',
            description: 'Because you get all the benefits of Fleequid, with maximum value.',
            item1: {
                title: 'Fees reduced by 50%',
                description: `Save on the buyer's fees for every vehicle purchased or auctioned during Black Week.`,
            },
            item2: {
                title: 'Valid on Buy Now and Auction offers',
                description: 'The discount applies to both immediate purchases and competitive auctions starting during Black Week.',
            },
            item3: {
                title: 'Wide choice of vehicles',
                description: 'Coaches, minibuses and school buses: every vehicle comes with our TrustReport, giving you all the information you need.',
            },
            item4: {
                title: 'Additional services and full support',
                description: 'With Fleequid you have access to a wide range of additional services and the reliability of a partner that supports you through every stage of the process.',
            },
        },
        sectionHowItWorks: {
            title: 'How does Black Week work?',
            item1: 'Log in with your account or create a new one. It only takes a few minutes.',
            item2: 'Browse the catalogue and use the filters to find the perfect vehicle for you.',
            item3: 'Complete your purchase and automatically save 50% on Fleequid fees.',
        },
        sectionBuy: {
            [SellingMethod.DirectSale]: {
                title: 'Buy vehicles with {br} “Buy Now”',
                description: 'Choose whether to buy immediately at the shown price or place an offer based on your budget.',
                cta: 'Show “Buy Now” vehicles',
            },
            [SellingMethod.Auction]: {
                title: 'Win vehicles {br} at auction',
                description: 'Place your bid and try to win your next vehicle.',
                cta: 'Show auction vehicles',
            },
        },
        sectionRegister: {
            title1: 'From 24 to 28 November',
            title2: 'Don’t miss the right vehicle on Fleequid',
            description: 'Log in or register now, the offer is valid only for a few days.',
            cta: 'Register now',
        },
    },
    fees: {
        orderSummaryRow: 'Fleequid fees',
        orderSummaryRowBlackWeek: 'Black Week fees',
        infoModal: {
            title: 'What Your Service Fee Covers',
            description: 'Your Fleequid service fee ensures a safe simple and transparent transaction:',
            item1: {
                title: 'Professional Inspection:',
                description: 'Every vehicle is carefully inspected by Fleequid through a clear standardized and independent process. Over 2200 vehicles have already been inspected.',
            },
            item2: {
                title: 'Verified information:',
                description: 'We verified every detail. Fleequid guarantees the accuracy of all vehicle data on our platform.',
            },
            item3: {
                title: 'Secure Payments:',
                description: 'Fleequid directly manages payments and all stages of the transaction, allowing you to purchase with complete security.',
            },
            item4: {
                title: 'Document and Process Security:',
                description: 'All vehicles on the platform are free of any liens, and Fleequid directly ensures the correct completion of the delivery process.',
            },
            more: 'Do you want to know more?',
            bottomNotice: '* excluding VAT, ** plus VAT if applicable',
        },
        infoTable: {
            col1: {
                header: 'Vehicle winning bid price (*)',
                row1: 'up to € 2,500',
                row2: 'from € 2,500 up to € 100,000',
                row3: '> € 100,000',
            },
            col2: {
                header: 'Fees per single vehicle (**)',
                row1: 'Fixed amount of {highlight}',
                row2: 'Previous bracket amount {highlight} on the value of the winning bid price',
                row3: 'Previous bracket amounts {highlight} on the portion of the winning bid price that exceeds € 100,000',
            },
        },
        description: {
            [FeeTarget.Buyer]: 'Total Fleequid fees included',
            [FeeTarget.Seller]: 'Your earnings (net of Fleequid fees)',
        },
        blackWeekDescription: 'Special Black Week',
        blackWeekOrderTotal: 'Special Black Week (Fleequid fees included)',
    },
    favorites: {
        tooltip: {
            loading: 'Loading',
            checked: 'This vehicle is in your favorite list',
            unchecked: 'Add this vehicle to your favorite list',
        },
        notifications: {
            add: {
                title: 'Vehicle added to favorites!',
                description: 'You have successfully saved {title} to your favorites list.',
            },
            delete: {
                title: 'Vehicle removed from favorites.',
                description: 'You have removed {title} from your favorites list.',
            },
        },
    },
    announcementBar: {
        vehiclesInspected: 'Over {count} vehicles professionally inspected',
        securePayments: 'Secure payments',
        warranty: 'Warranty available up to 12 months',
        callForAdvice: 'Call for advice:',
    },
};

export default en;
